'use client';

import { type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Arrow, Circle, Group, Label, Layer, Line, Rect, Stage, Tag, Text } from 'react-konva';

import type {
  ConfiguratorState,
  HolePoint,
  OutlineNotchEdge,
  ProductInternalCut,
} from './lib/configurator-math';
import {
  getEffectiveLimits,
  getHoleHalfExtents,
  getHoleLayout,
  getProductBendLines,
  getProductInternalCuts,
  getProductOutlineNotches,
  getProductOutlineSettings,
  getSelectedOptions,
} from './lib/configurator-math';
import { buildOutlineGeometry, outlineGeometryToScreenPoints } from './lib/configurator-outline';
import { PlatePreview3D } from './plate-preview-3d';

type ViewMode = '2d' | '3d';

type PlatePreviewProps = {
  config: ConfiguratorState;
  viewMode: ViewMode;
  update?: (patch: Partial<ConfiguratorState>) => void;
  selectedHoleIndexes?: number[];
  activeHoleIndex?: number | null;
  onHoleSelectionChange?: (indexes: number[], activeIndex: number | null) => void;
  selectedInternalCutId?: string | null;
  onInternalCutSelectionChange?: (id: string | null) => void;
  selectedOutlineNotchId?: string | null;
  onOutlineNotchSelectionChange?: (id: string | null) => void;
  snapStepMm?: number;
  showBendGuides?: boolean;
};

type Point = [number, number];
type HolePosition = { xMm: number; yMm: number };
type SelectionBox = { startX: number; startY: number; x: number; y: number };
type HoleSpacingDimension = {
  key: string;
  points: number[];
  guidePoints: number[][];
  label: string;
  labelX: number;
  labelY: number;
};

type DragSnapshot = {
  anchorIndex: number;
  indexes: number[];
  positions: HolePosition[];
};
type CutPosition = { id: string; xMm: number; yMm: number };

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function roundGeometryMm(value: number) {
  return Math.round(value * 1000) / 1000;
}

function formatMm(value: number) {
  return `${new Intl.NumberFormat('sk-SK', {
    maximumFractionDigits: 3,
  }).format(value)}mm`;
}

function isHorizontalOutlineEdge(edge: OutlineNotchEdge) {
  return edge === 'top' || edge === 'bottom';
}

function getPlanCornerRadius(config: ConfiguratorState) {
  return clamp(config.edgeRadiusMm, 0, Math.min(config.widthMm, config.lengthMm) / 2);
}

function fitDrawing(size: { width: number; height: number }, config: ConfiguratorState) {
  const paddingX = 116;
  const paddingTop = 76;
  const paddingBottom = 138;
  const availableWidth = Math.max(size.width - paddingX * 2, 220);
  const availableHeight = Math.max(size.height - paddingTop - paddingBottom, 180);
  const aspect = config.widthMm / config.lengthMm;
  const width = Math.min(availableWidth, availableHeight * aspect);
  const height = width / aspect;

  return {
    width,
    height,
    left: size.width / 2 - width / 2,
    top: paddingTop + availableHeight / 2 - height / 2,
  };
}

function getShapeTaper(config: ConfiguratorState, drawing: ReturnType<typeof fitDrawing>) {
  const { product } = getSelectedOptions(config);
  return product.visual.kind === 'trapezoid'
    ? drawing.width * 0.14
    : product.visual.kind === 'freeform'
      ? drawing.width * 0.08
      : 0;
}

function isDoubleLinkProduct(config: ConfiguratorState) {
  return getSelectedOptions(config).product.visual.kind === 'double-link';
}

function getDoubleLinkMetrics(config: ConfiguratorState, drawing: ReturnType<typeof fitDrawing>) {
  const outline = getProductOutlineSettings(config);
  const primaryTopNotch = getProductOutlineNotches(config).find(notch => notch.edge === 'top');
  const widthScale = drawing.width / Math.max(config.widthMm, 1);
  const heightScale = drawing.height / Math.max(config.lengthMm, 1);
  const radius = Math.min(
    outline.outerRadiusMm * Math.min(widthScale, heightScale),
    drawing.width * 0.22,
    drawing.height * 0.18,
  );
  const notchCenter =
    drawing.left +
    ((primaryTopNotch?.centerMm ?? config.widthMm / 2) / Math.max(config.widthMm, 1)) *
      drawing.width;
  const centerGap = Math.min(
    Math.max((primaryTopNotch?.widthMm ?? outline.centerGapMm) * widthScale, 4),
    drawing.width * 0.85,
  );
  const notchDepth = Math.min(
    Math.max((primaryTopNotch?.depthMm ?? outline.topNotchDepthMm) * heightScale, radius * 1.35),
    drawing.height * 0.75,
  );

  return { radius, centerGap, notchDepth, notchCenter };
}

function makePositionShapePoints(
  config: ConfiguratorState,
  drawing: ReturnType<typeof fitDrawing>,
): Point[] {
  const taper = getShapeTaper(config, drawing);
  const left = drawing.left;
  const right = drawing.left + drawing.width;
  const top = drawing.top;
  const bottom = drawing.top + drawing.height;

  return [
    [left + taper, top],
    [right - taper * 0.45, top],
    [right, bottom],
    [left, bottom],
  ];
}

function makeShapePoints(
  config: ConfiguratorState,
  drawing: ReturnType<typeof fitDrawing>,
): Point[] {
  return outlineGeometryToScreenPoints(buildOutlineGeometry(config), drawing);
}

function flatten(points: Point[]) {
  return points.flatMap(point => point);
}

function addPathPoint(points: number[], x: number, y: number) {
  const lastX = points[points.length - 2];
  const lastY = points[points.length - 1];
  if (Math.abs((lastX ?? Number.NaN) - x) < 0.01 && Math.abs((lastY ?? Number.NaN) - y) < 0.01) {
    return;
  }
  points.push(x, y);
}

function addPathArc(
  points: number[],
  centerX: number,
  centerY: number,
  radius: number,
  start: number,
  end: number,
  steps = 7,
) {
  if (radius <= 0.01) return;
  for (let index = 0; index <= steps; index += 1) {
    const angle = start + ((end - start) * index) / steps;
    addPathPoint(points, centerX + Math.cos(angle) * radius, centerY + Math.sin(angle) * radius);
  }
}

function interpolatePoint(points: Point[], xRatio: number, yRatio: number) {
  const [tl, tr, br, bl] = points;
  const topX = tl[0] + (tr[0] - tl[0]) * xRatio;
  const topY = tl[1] + (tr[1] - tl[1]) * xRatio;
  const bottomX = bl[0] + (br[0] - bl[0]) * xRatio;
  const bottomY = bl[1] + (br[1] - bl[1]) * xRatio;

  return {
    x: topX + (bottomX - topX) * yRatio,
    y: topY + (bottomY - topY) * yRatio,
  };
}

function isAdditiveSelection(event: any) {
  return Boolean(event.evt?.ctrlKey || event.evt?.metaKey || event.evt?.shiftKey);
}

function isEditableKeyboardTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;

  return Boolean(target.closest('input, textarea, select, button, [contenteditable="true"]'));
}

const PREVIEW_MEASURE_LABEL = {
  fill: '#ffffff',
  stroke: '#ead6cf',
  cornerRadius: 4,
  shadowColor: 'rgba(0,0,0,0.09)',
  shadowBlur: 5,
  shadowOffsetY: 1.5,
  padding: 6,
  fontSize: 10,
  fontStyle: '600',
};

function DimensionLabel({
  x,
  y,
  label,
  rotation = 0,
  tone = 'default',
}: {
  x: number;
  y: number;
  label: string;
  rotation?: number;
  tone?: 'default' | 'active';
}) {
  return (
    <Label x={x} y={y} rotation={rotation} listening={false}>
      <Tag
        fill={PREVIEW_MEASURE_LABEL.fill}
        stroke={PREVIEW_MEASURE_LABEL.stroke}
        cornerRadius={PREVIEW_MEASURE_LABEL.cornerRadius}
        shadowColor={PREVIEW_MEASURE_LABEL.shadowColor}
        shadowBlur={PREVIEW_MEASURE_LABEL.shadowBlur}
        shadowOffsetY={PREVIEW_MEASURE_LABEL.shadowOffsetY}
      />
      <Text
        text={label}
        padding={PREVIEW_MEASURE_LABEL.padding}
        fontSize={PREVIEW_MEASURE_LABEL.fontSize}
        fontStyle={PREVIEW_MEASURE_LABEL.fontStyle}
        fill={tone === 'active' ? '#c22a05' : '#111'}
      />
    </Label>
  );
}

function MeasurementLabel({
  x,
  y,
  label,
  refKey,
  guideRefs,
}: {
  x: number;
  y: number;
  label: string;
  refKey?: string;
  guideRefs?: MutableRefObject<Record<string, any>>;
}) {
  return (
    <Label
      ref={node => {
        if (node && refKey && guideRefs) guideRefs.current[refKey] = node;
      }}
      x={x}
      y={y}
      listening={false}
    >
      <Tag
        fill={PREVIEW_MEASURE_LABEL.fill}
        stroke={PREVIEW_MEASURE_LABEL.stroke}
        cornerRadius={PREVIEW_MEASURE_LABEL.cornerRadius}
        shadowColor={PREVIEW_MEASURE_LABEL.shadowColor}
        shadowBlur={PREVIEW_MEASURE_LABEL.shadowBlur}
        shadowOffsetY={PREVIEW_MEASURE_LABEL.shadowOffsetY}
      />
      <Text
        text={label}
        padding={PREVIEW_MEASURE_LABEL.padding}
        fontSize={PREVIEW_MEASURE_LABEL.fontSize}
        fontStyle={PREVIEW_MEASURE_LABEL.fontStyle}
        fill="#c22a05"
      />
    </Label>
  );
}

function getHoleRenderSize(hole: HolePoint, radius: number) {
  const width = Math.max(3, radius * 2);
  if (hole.shape === 'round') {
    return { width, height: width, cornerRadius: radius };
  }

  const scale = width / Math.max(hole.slotWidthMm, 1);
  const slotLength = Math.max(width, hole.slotLengthMm * scale);

  return {
    width: slotLength,
    height: width,
    cornerRadius: hole.shape === 'slot' ? width / 2 : Math.min(width * 0.1, 3),
  };
}

function HoleGlyph({
  hole,
  radius,
  isActive = false,
  isSelected = false,
}: {
  hole: HolePoint;
  radius: number;
  isActive?: boolean;
  isSelected?: boolean;
}) {
  const size = getHoleRenderSize(hole, radius);
  const centerLineX = Math.max(size.width * 0.34, radius * 0.7);
  const centerLineY = Math.max(size.height * 0.34, radius * 0.7);

  return (
    <Group rotation={hole.shape !== 'round' ? hole.slotAngleDeg : 0}>
      {hole.shape !== 'round' ? (
        <>
          <Rect
            x={-size.width / 2 - 3}
            y={-size.height / 2 + 3}
            width={size.width}
            height={size.height}
            cornerRadius={size.cornerRadius}
            fill="#111"
            opacity={0.12}
            listening={false}
          />
          <Rect
            x={-size.width / 2}
            y={-size.height / 2}
            width={size.width}
            height={size.height}
            cornerRadius={size.cornerRadius}
            fill="#fff"
            stroke={isSelected ? '#e63208' : '#181818'}
            strokeWidth={isActive ? 3 : 2.4}
            listening={false}
            perfectDrawEnabled={false}
          />
        </>
      ) : (
        <>
          <Circle radius={radius + 5} fill={isSelected ? '#e63208' : '#111'} opacity={0.16} />
          <Circle
            radius={radius}
            fill="#fff"
            stroke={isSelected ? '#e63208' : '#181818'}
            strokeWidth={isActive ? 3 : 2.4}
            listening={false}
            perfectDrawEnabled={false}
          />
        </>
      )}
      <Circle
        radius={Math.min(radius * 0.45, 10)}
        stroke="#d7d7d7"
        strokeWidth={1}
        listening={false}
        perfectDrawEnabled={false}
      />
      <Line
        points={[-centerLineX, 0, centerLineX, 0]}
        stroke={isSelected ? '#e63208' : '#454545'}
        strokeWidth={1}
        opacity={0.72}
        listening={false}
        perfectDrawEnabled={false}
      />
      <Line
        points={[0, -centerLineY, 0, centerLineY]}
        stroke={isSelected ? '#e63208' : '#454545'}
        strokeWidth={1}
        opacity={0.72}
        listening={false}
        perfectDrawEnabled={false}
      />
    </Group>
  );
}

function InternalCutGlyph({
  width,
  height,
  orientation,
  shape,
  isSelected = false,
  angleDeg = 0,
  cornerRadius = 0,
}: {
  width: number;
  height: number;
  orientation: 'horizontal' | 'vertical' | 'custom';
  shape: 'slot' | 'round' | 'rect';
  isSelected?: boolean;
  angleDeg?: number;
  cornerRadius?: number;
}) {
  const renderCornerRadius = Math.min(Math.max(cornerRadius, 0), Math.min(width, height) / 2);
  const centerLineX = orientation === 'horizontal' ? width * 0.42 : width * 0.8;
  const centerLineY = orientation === 'vertical' ? height * 0.42 : height * 0.8;
  const renderWidth = shape === 'round' ? Math.min(width, height) : width;
  const renderHeight = shape === 'round' ? Math.min(width, height) : height;

  return (
    <Group rotation={shape === 'round' ? 0 : angleDeg}>
      <Rect
        x={-renderWidth / 2}
        y={-renderHeight / 2}
        width={renderWidth}
        height={renderHeight}
        cornerRadius={renderCornerRadius}
        fill="#fff"
        stroke={isSelected ? '#e63208' : '#181818'}
        strokeWidth={isSelected ? 3 : 2}
        listening={false}
        perfectDrawEnabled={false}
      />
      <Line
        points={[-centerLineX / 2, 0, centerLineX / 2, 0]}
        stroke={isSelected ? '#e63208' : '#7b7b7b'}
        strokeWidth={1}
        opacity={isSelected ? 0.82 : 0.58}
        listening={false}
      />
      <Line
        points={[0, -centerLineY / 2, 0, centerLineY / 2]}
        stroke={isSelected ? '#e63208' : '#7b7b7b'}
        strokeWidth={1}
        opacity={isSelected ? 0.82 : 0.58}
        listening={false}
      />
    </Group>
  );
}

function PlateHole({
  hole,
  index,
  isActive,
  isSelected,
  point,
  radius,
  onClick,
  onDragEnd,
  onDragMove,
  onDragStart,
  onNodeReady,
  onPress,
}: {
  hole: HolePoint;
  index: number;
  isActive: boolean;
  isSelected: boolean;
  point: { x: number; y: number };
  radius: number;
  onClick: (index: number, event: any) => void;
  onDragEnd: (index: number, x: number, y: number, node: any) => void;
  onDragMove: (index: number, x: number, y: number, node: any) => void;
  onDragStart: (index: number, node: any) => void;
  onNodeReady: (index: number, node: any) => void;
  onPress: (index: number, event: any) => void;
}) {
  const visualSize = getHoleRenderSize(hole, radius);
  const hitPadding = hole.shape !== 'round' ? 5 : 7;
  const hitSize = {
    width: visualSize.width + hitPadding * 2,
    height: visualSize.height + hitPadding * 2,
    cornerRadius: visualSize.cornerRadius + hitPadding,
  };

  return (
    <Group
      ref={node => {
        if (node) onNodeReady(index, node);
      }}
      transformsEnabled="position"
      x={point.x}
      y={point.y}
      draggable
      onClick={event => onClick(index, event)}
      onTap={event => onClick(index, event)}
      onMouseDown={event => onPress(index, event)}
      onTouchStart={event => onPress(index, event)}
      onDragStart={event => {
        onDragStart(index, event.currentTarget);
        const container = event.currentTarget.getStage()?.container();
        if (container) container.style.cursor = 'grabbing';
      }}
      onDragMove={event =>
        onDragMove(index, event.currentTarget.x(), event.currentTarget.y(), event.currentTarget)
      }
      onDragEnd={event => {
        onDragEnd(index, event.currentTarget.x(), event.currentTarget.y(), event.currentTarget);
        const container = event.currentTarget.getStage()?.container();
        if (container) container.style.cursor = 'grab';
      }}
      onMouseEnter={event => {
        const container = event.currentTarget.getStage()?.container();
        if (container) container.style.cursor = 'grab';
      }}
      onMouseLeave={event => {
        const container = event.currentTarget.getStage()?.container();
        if (container) container.style.cursor = 'default';
      }}
    >
      {hole.shape !== 'round' ? (
        <Group rotation={hole.slotAngleDeg}>
          <Rect
            x={-hitSize.width / 2}
            y={-hitSize.height / 2}
            width={hitSize.width}
            height={hitSize.height}
            cornerRadius={hitSize.cornerRadius}
            fill="rgba(230,50,8,0.001)"
          />
        </Group>
      ) : (
        <Circle radius={radius + hitPadding} fill="rgba(230,50,8,0.001)" />
      )}
      <HoleGlyph hole={hole} radius={radius} isActive={isActive} isSelected={isSelected} />
    </Group>
  );
}

function PlatePreview2D({
  config,
  update,
  selectedHoleIndexes: controlledSelectedHoleIndexes,
  activeHoleIndex: controlledActiveHoleIndex,
  onHoleSelectionChange,
  selectedInternalCutId: controlledSelectedInternalCutId,
  onInternalCutSelectionChange,
  selectedOutlineNotchId: controlledSelectedOutlineNotchId,
  onOutlineNotchSelectionChange,
  snapStepMm = 1,
  showBendGuides = false,
  isActive = true,
}: {
  config: ConfiguratorState;
  update?: (patch: Partial<ConfiguratorState>) => void;
  selectedHoleIndexes?: number[];
  activeHoleIndex?: number | null;
  onHoleSelectionChange?: (indexes: number[], activeIndex: number | null) => void;
  selectedInternalCutId?: string | null;
  onInternalCutSelectionChange?: (id: string | null) => void;
  selectedOutlineNotchId?: string | null;
  onOutlineNotchSelectionChange?: (id: string | null) => void;
  snapStepMm?: number;
  showBendGuides?: boolean;
  isActive?: boolean;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const activeGuideRefs = useRef<Record<string, any>>({});
  const activeLabelValuesRef = useRef<Record<string, string>>({});
  const interactiveLayerRef = useRef<any>(null);
  const spacingMeasurementGroupRef = useRef<any>(null);
  const holeNodeRefs = useRef<Record<number, any>>({});
  const cutNodeRefs = useRef<Record<string, any>>({});
  const cutGuideRefs = useRef<Record<string, any>>({});
  const cutLabelValuesRef = useRef<Record<string, string>>({});
  const cutGuideDrawFrameRef = useRef<number | null>(null);
  const pendingDragPositionsRef = useRef<HolePosition[] | null>(null);
  const pendingKeyboardPositionsRef = useRef<HolePosition[] | null>(null);
  const pendingCutKeyboardPositionRef = useRef<CutPosition | null>(null);
  const keyboardCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cutKeyboardCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragSnapshotRef = useRef<DragSnapshot | null>(null);
  const selectedIndexesRef = useRef<number[]>([]);
  const selectionBaseIndexesRef = useRef<number[]>([]);
  const didDragRef = useRef(false);
  const [size, setSize] = useState({ width: 760, height: 430 });
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  const [localSelectedHoleIndexes, setLocalSelectedHoleIndexes] = useState<number[]>([]);
  const [localActiveHoleIndex, setLocalActiveHoleIndex] = useState<number | null>(null);
  const [localSelectedInternalCutId, setLocalSelectedInternalCutId] = useState<string | null>(null);
  const [activeBendLineId, setActiveBendLineId] = useState<string | null>(null);
  const [localShowBendGuides, setLocalShowBendGuides] = useState(false);
  const [activeShapeHandle, setActiveShapeHandle] = useState<null | 'top-notch'>(null);
  const [isMeasurementDragActive, setIsMeasurementDragActive] = useState(false);
  const [outlineNotchPreview, setOutlineNotchPreview] = useState<ReturnType<
    typeof getProductOutlineNotches
  > | null>(null);
  const { finish } = getSelectedOptions(config);
  const limits = useMemo(() => getEffectiveLimits(config), [config]);
  const holes = useMemo(() => getHoleLayout(config), [config]);
  const internalCuts = useMemo(() => getProductInternalCuts(config), [config]);
  const outlineNotches = useMemo(() => getProductOutlineNotches(config), [config]);
  const bendLines = useMemo(() => getProductBendLines(config), [config]);
  const visibleBendGuides = showBendGuides || localShowBendGuides;
  const selectedHoleIndexes = useMemo(
    () =>
      [...new Set(controlledSelectedHoleIndexes ?? localSelectedHoleIndexes)]
        .filter(index => index >= 0 && index < holes.length)
        .sort((a, b) => a - b),
    [controlledSelectedHoleIndexes, localSelectedHoleIndexes, holes.length],
  );
  const activeHoleIndex =
    controlledActiveHoleIndex !== undefined &&
    controlledActiveHoleIndex !== null &&
    selectedHoleIndexes.includes(controlledActiveHoleIndex)
      ? controlledActiveHoleIndex
      : localActiveHoleIndex !== null && selectedHoleIndexes.includes(localActiveHoleIndex)
        ? localActiveHoleIndex
        : (selectedHoleIndexes[0] ?? null);
  const selectedInternalCutId =
    controlledSelectedInternalCutId !== undefined
      ? controlledSelectedInternalCutId
      : localSelectedInternalCutId;
  const selectedOutlineNotchId = controlledSelectedOutlineNotchId ?? null;
  const previewOutlineNotches = outlineNotchPreview ?? outlineNotches;
  const previewConfig = useMemo(
    () =>
      outlineNotchPreview
        ? {
            ...config,
            outlineNotches: outlineNotchPreview,
          }
        : config,
    [config, outlineNotchPreview],
  );

  useEffect(() => {
    if (bendLines.length > 0) return;
    setLocalShowBendGuides(false);
    setActiveBendLineId(null);
  }, [bendLines.length]);

  const requestCutGuideDraw = () => {
    if (typeof window === 'undefined') {
      interactiveLayerRef.current?.batchDraw();
      return;
    }
    if (cutGuideDrawFrameRef.current !== null) return;
    cutGuideDrawFrameRef.current = window.requestAnimationFrame(() => {
      cutGuideDrawFrameRef.current = null;
      interactiveLayerRef.current?.batchDraw();
    });
  };

  const setInternalCutSelection = (id: string | null) => {
    if (controlledSelectedInternalCutId === undefined) setLocalSelectedInternalCutId(id);
    onInternalCutSelectionChange?.(id);
  };
  const setOutlineNotchSelection = (id: string | null) => {
    onOutlineNotchSelectionChange?.(id);
  };

  const setSelection = (indexes: number[], activeIndex: number | null = indexes[0] ?? null) => {
    const nextIndexes = [...new Set(indexes)]
      .filter(index => index >= 0 && index < holes.length)
      .sort((a, b) => a - b);
    const nextActiveIndex =
      activeIndex !== null && nextIndexes.includes(activeIndex)
        ? activeIndex
        : (nextIndexes[0] ?? null);

    if (nextIndexes.length) {
      setInternalCutSelection(null);
      setOutlineNotchSelection(null);
    }
    selectedIndexesRef.current = nextIndexes;
    if (controlledSelectedHoleIndexes === undefined) setLocalSelectedHoleIndexes(nextIndexes);
    if (controlledActiveHoleIndex === undefined) setLocalActiveHoleIndex(nextActiveIndex);
    onHoleSelectionChange?.(nextIndexes, nextActiveIndex);
  };

  useEffect(() => {
    selectedIndexesRef.current = selectedHoleIndexes;
  }, [selectedHoleIndexes]);

  useEffect(() => {
    setSelection(selectedHoleIndexes, activeHoleIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holes.length]);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      const width = Math.round(rect.width || node.clientWidth);
      if (width < 1) return;
      const height = Math.max(
        360,
        Math.round(rect.height || node.clientHeight || Math.min(width * 0.56, 520)),
      );
      setSize({ width, height });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(
    () => () => {
      if (cutGuideDrawFrameRef.current !== null) {
        window.cancelAnimationFrame(cutGuideDrawFrameRef.current);
        cutGuideDrawFrameRef.current = null;
      }
    },
    [],
  );

  const dots = useMemo(() => {
    const result: Array<{ x: number; y: number }> = [];
    for (let x = 28; x < size.width; x += 34) {
      for (let y = 28; y < size.height - 24; y += 34) {
        result.push({ x, y });
      }
    }
    return result;
  }, [size]);

  const drawing = fitDrawing(size, previewConfig);
  const shapePoints = makeShapePoints(previewConfig, drawing);
  const positionShapePoints = makePositionShapePoints(previewConfig, drawing);
  const {
    left: drawingLeft,
    top: drawingTop,
    width: drawingWidth,
    height: drawingHeight,
  } = drawing;
  const constructionGrid = useMemo(() => {
    const step = snapStepMm > 0 ? snapStepMm : 10;
    const vertical: number[] = [];
    const horizontal: number[] = [];

    for (let xMm = step; xMm < config.widthMm; xMm += step) {
      vertical.push(drawingLeft + (xMm / config.widthMm) * drawingWidth);
    }
    for (let yMm = step; yMm < config.lengthMm; yMm += step) {
      horizontal.push(drawingTop + (yMm / config.lengthMm) * drawingHeight);
    }

    return { vertical, horizontal };
  }, [
    config.widthMm,
    config.lengthMm,
    drawingLeft,
    drawingTop,
    drawingWidth,
    drawingHeight,
    snapStepMm,
  ]);
  const drawingScale = drawing.width / Math.max(config.widthMm, 1);
  const doubleLinkMetrics = isDoubleLinkProduct(previewConfig)
    ? getDoubleLinkMetrics(previewConfig, drawing)
    : null;
  const getHoleRadius = (hole: HolePoint) => Math.max(1.5, (hole.diameterMm / 2) * drawingScale);
  const getHoleHitRadius = (hole: HolePoint) => {
    const extents = getHoleHalfExtents(hole);
    return Math.max(1.5, Math.max(extents.xMm, extents.yMm) * drawingScale);
  };
  const getCutRenderBoxAt = (cut: ProductInternalCut, xMm = cut.xMm, yMm = cut.yMm) => {
    const widthScale = drawing.width / Math.max(config.widthMm, 1);
    const heightScale = drawing.height / Math.max(config.lengthMm, 1);
    const renderWidthMm = cut.shape === 'round' ? cut.widthMm : cut.lengthMm;
    const renderHeightMm = cut.shape === 'round' ? cut.widthMm : cut.widthMm;
    const renderWidth = Math.max(3, renderWidthMm * widthScale);
    const renderHeight = Math.max(3, renderHeightMm * heightScale);
    const angleDeg = cut.shape === 'round' ? 0 : cut.angleDeg;
    const angle = (angleDeg * Math.PI) / 180;
    const cos = Math.abs(Math.cos(angle));
    const sin = Math.abs(Math.sin(angle));
    const width = renderWidth * cos + renderHeight * sin;
    const height = renderWidth * sin + renderHeight * cos;
    const cornerRadius = Math.min(
      cut.cornerRadiusMm * Math.min(widthScale, heightScale),
      Math.min(renderWidth, renderHeight) / 2,
    );
    const x = drawing.left + (xMm / Math.max(config.widthMm, 1)) * drawing.width;
    const y = drawing.top + (yMm / Math.max(config.lengthMm, 1)) * drawing.height;

    return {
      x,
      y,
      width,
      height,
      boxWidthMm: renderWidthMm,
      boxHeightMm: renderHeightMm,
      renderWidth,
      renderHeight,
      angleDeg,
      cornerRadius,
      left: x - width / 2,
      right: x + width / 2,
      top: y - height / 2,
      bottom: y + height / 2,
    };
  };
  const getCutRenderBox = (cut: ProductInternalCut) => getCutRenderBoxAt(cut);
  const getCutHalfExtentsMm = (cut: ProductInternalCut) => {
    if (cut.shape === 'round') {
      const radius = cut.widthMm / 2;
      return { xMm: radius, yMm: radius };
    }

    const angle = (cut.angleDeg * Math.PI) / 180;
    const cos = Math.abs(Math.cos(angle));
    const sin = Math.abs(Math.sin(angle));

    return {
      xMm: (cut.lengthMm / 2) * cos + (cut.widthMm / 2) * sin,
      yMm: (cut.lengthMm / 2) * sin + (cut.widthMm / 2) * cos,
    };
  };
  const getCutEdgeMeasurements = (cut: ProductInternalCut, position: HolePosition) => {
    const extents = getCutHalfExtentsMm(cut);

    return {
      leftMm: Math.max(0, position.xMm - extents.xMm),
      rightMm: Math.max(0, config.widthMm - position.xMm - extents.xMm),
      topMm: Math.max(0, position.yMm - extents.yMm),
      bottomMm: Math.max(0, config.lengthMm - position.yMm - extents.yMm),
    };
  };
  const activeHole = activeHoleIndex !== null ? holes[activeHoleIndex] : null;
  const activePoint = activeHole
    ? interpolatePoint(positionShapePoints, activeHole.xRatio, activeHole.yRatio)
    : null;
  const selectedMeasurementPoints = selectedHoleIndexes
    .map(index => {
      const hole = holes[index];
      if (!hole) return null;
      const point = interpolatePoint(positionShapePoints, hole.xRatio, hole.yRatio);
      const radius = getHoleHitRadius(hole);
      return { index, hole, ...point, radius };
    })
    .filter(
      (point): point is { index: number; hole: HolePoint; x: number; y: number; radius: number } =>
        Boolean(point),
    );
  const selectedHoleMeasurements = (() => {
    if (selectedMeasurementPoints.length < 2) return [];

    const dimensions: HoleSpacingDimension[] = [];
    const usedPairs = new Set<string>();
    const axisToleranceMm = Math.max(2, snapStepMm || 0);
    const maxDimensions = 8;
    const addDimension = (
      from: (typeof selectedMeasurementPoints)[number],
      to: (typeof selectedMeasurementPoints)[number],
      orientation: 'horizontal' | 'vertical' | 'diagonal',
    ) => {
      if (dimensions.length >= maxDimensions) return;

      const pairKey = [from.index, to.index].sort((a, b) => a - b).join('-');
      if (usedPairs.has(pairKey)) return;
      usedPairs.add(pairKey);

      if (orientation === 'horizontal') {
        const lineY = Math.min(from.y - from.radius, to.y - to.radius) - 24;
        dimensions.push({
          key: `hole-spacing-x-${pairKey}`,
          points: [from.x, lineY, to.x, lineY],
          guidePoints: [
            [from.x, from.y, from.x, lineY],
            [to.x, to.y, to.x, lineY],
          ],
          label: formatMm(Math.abs(to.hole.xMm - from.hole.xMm)),
          labelX: (from.x + to.x) / 2 - 18,
          labelY: lineY - 16,
        });
        return;
      }

      if (orientation === 'vertical') {
        const lineX = Math.max(from.x + from.radius, to.x + to.radius) + 24;
        dimensions.push({
          key: `hole-spacing-y-${pairKey}`,
          points: [lineX, from.y, lineX, to.y],
          guidePoints: [
            [from.x, from.y, lineX, from.y],
            [to.x, to.y, lineX, to.y],
          ],
          label: formatMm(Math.abs(to.hole.yMm - from.hole.yMm)),
          labelX: lineX + 8,
          labelY: (from.y + to.y) / 2 - 7,
        });
        return;
      }

      const distanceMm = Math.hypot(to.hole.xMm - from.hole.xMm, to.hole.yMm - from.hole.yMm);
      dimensions.push({
        key: `hole-spacing-diagonal-${pairKey}`,
        points: [from.x, from.y, to.x, to.y],
        guidePoints: [],
        label: formatMm(distanceMm),
        labelX: (from.x + to.x) / 2 + 8,
        labelY: (from.y + to.y) / 2 - 16,
      });
    };

    const buildAxisGroups = (axis: 'xMm' | 'yMm') => {
      const groups: Array<typeof selectedMeasurementPoints> = [];
      [...selectedMeasurementPoints]
        .sort((a, b) => a.hole[axis] - b.hole[axis])
        .forEach(point => {
          const group = groups.find(
            current => Math.abs(current[0].hole[axis] - point.hole[axis]) <= axisToleranceMm,
          );
          if (group) group.push(point);
          else groups.push([point]);
        });

      return groups.filter(group => group.length > 1);
    };

    buildAxisGroups('yMm').forEach(group => {
      group
        .sort((a, b) => a.hole.xMm - b.hole.xMm)
        .forEach((point, index, sorted) => {
          const nextPoint = sorted[index + 1];
          if (nextPoint) addDimension(point, nextPoint, 'horizontal');
        });
    });

    buildAxisGroups('xMm').forEach(group => {
      group
        .sort((a, b) => a.hole.yMm - b.hole.yMm)
        .forEach((point, index, sorted) => {
          const nextPoint = sorted[index + 1];
          if (nextPoint) addDimension(point, nextPoint, 'vertical');
        });
    });

    if (!dimensions.length && selectedMeasurementPoints.length === 2) {
      addDimension(selectedMeasurementPoints[0], selectedMeasurementPoints[1], 'diagonal');
    }

    return dimensions;
  })();
  const selectedCut =
    selectedInternalCutId !== null
      ? (internalCuts.find(cut => cut.id === selectedInternalCutId) ?? null)
      : null;
  const selectedCutBox = selectedCut ? getCutRenderBox(selectedCut) : null;
  const selectedCutMeasurements =
    selectedCut && selectedCutBox
      ? getCutEdgeMeasurements(selectedCut, { xMm: selectedCut.xMm, yMm: selectedCut.yMm })
      : null;
  const getNotchAxisSize = (edge: OutlineNotchEdge) =>
    isHorizontalOutlineEdge(edge) ? config.widthMm : config.lengthMm;
  const getNotchDepthAxisSize = (edge: OutlineNotchEdge) =>
    isHorizontalOutlineEdge(edge) ? config.lengthMm : config.widthMm;
  const getNotchScreenMetrics = (notch: ReturnType<typeof getProductOutlineNotches>[number]) => {
    const edge = notch.edge;
    const isHorizontal = isHorizontalOutlineEdge(edge);
    const axisScale = isHorizontal
      ? drawing.width / Math.max(config.widthMm, 1)
      : drawing.height / Math.max(config.lengthMm, 1);
    const depthScale = isHorizontal
      ? drawing.height / Math.max(config.lengthMm, 1)
      : drawing.width / Math.max(config.widthMm, 1);
    const screenWidth = Math.max(4, notch.widthMm * axisScale);
    const screenDepth = Math.max(2, notch.depthMm * depthScale);
    const cornerScale = Math.min(axisScale, depthScale);
    const innerRadius = Math.min(
      Math.max(0, notch.radiusMm * cornerScale),
      screenWidth / 2,
      screenDepth / 2,
    );
    const center = isHorizontal
      ? drawing.left + notch.centerRatio * drawing.width
      : drawing.top + notch.centerRatio * drawing.height;
    const edgeStart = isHorizontal ? drawing.left : drawing.top;
    const edgeEnd = edgeStart + (isHorizontal ? drawing.width : drawing.height);
    const mouthRadius = Math.min(
      Math.max(0, notch.mouthRadiusMm * cornerScale),
      screenDepth * 0.45,
      Math.max(0, center - screenWidth / 2 - edgeStart),
      Math.max(0, edgeEnd - (center + screenWidth / 2)),
    );
    const root = {
      x: edge === 'right' ? drawing.left + drawing.width : drawing.left,
      y: edge === 'bottom' ? drawing.top + drawing.height : drawing.top,
    };

    if (edge === 'top' || edge === 'bottom') {
      const innerY = edge === 'bottom' ? root.y - screenDepth : root.y + screenDepth;
      const left = center - screenWidth / 2;
      const right = center + screenWidth / 2;
      const outlinePoints: number[] = [];

      if (edge === 'top') {
        addPathPoint(outlinePoints, left - mouthRadius, root.y);
        if (mouthRadius > 0) {
          addPathArc(
            outlinePoints,
            left - mouthRadius,
            root.y + mouthRadius,
            mouthRadius,
            -Math.PI / 2,
            0,
          );
        }
        if (innerRadius > 0) {
          addPathPoint(outlinePoints, left, innerY - innerRadius);
          addPathArc(
            outlinePoints,
            left + innerRadius,
            innerY - innerRadius,
            innerRadius,
            Math.PI,
            Math.PI / 2,
          );
          addPathPoint(outlinePoints, right - innerRadius, innerY);
          addPathArc(
            outlinePoints,
            right - innerRadius,
            innerY - innerRadius,
            innerRadius,
            Math.PI / 2,
            0,
          );
        } else {
          addPathPoint(outlinePoints, left, innerY);
          addPathPoint(outlinePoints, right, innerY);
        }
        if (mouthRadius > 0) {
          addPathPoint(outlinePoints, right, root.y + mouthRadius);
          addPathArc(
            outlinePoints,
            right + mouthRadius,
            root.y + mouthRadius,
            mouthRadius,
            Math.PI,
            Math.PI * 1.5,
          );
        } else {
          addPathPoint(outlinePoints, right, root.y);
        }
      } else {
        addPathPoint(outlinePoints, right + mouthRadius, root.y);
        if (mouthRadius > 0) {
          addPathArc(
            outlinePoints,
            right + mouthRadius,
            root.y - mouthRadius,
            mouthRadius,
            Math.PI / 2,
            Math.PI,
          );
        }
        if (innerRadius > 0) {
          addPathPoint(outlinePoints, right, innerY + innerRadius);
          addPathArc(
            outlinePoints,
            right - innerRadius,
            innerY + innerRadius,
            innerRadius,
            0,
            -Math.PI / 2,
          );
          addPathPoint(outlinePoints, left + innerRadius, innerY);
          addPathArc(
            outlinePoints,
            left + innerRadius,
            innerY + innerRadius,
            innerRadius,
            -Math.PI / 2,
            -Math.PI,
          );
        } else {
          addPathPoint(outlinePoints, right, innerY);
          addPathPoint(outlinePoints, left, innerY);
        }
        if (mouthRadius > 0) {
          addPathPoint(outlinePoints, left, root.y - mouthRadius);
          addPathArc(
            outlinePoints,
            left - mouthRadius,
            root.y - mouthRadius,
            mouthRadius,
            0,
            Math.PI / 2,
          );
        } else {
          addPathPoint(outlinePoints, left, root.y);
        }
      }

      return {
        edge,
        isHorizontal,
        centerX: center,
        centerY: innerY,
        width: screenWidth,
        depth: screenDepth,
        left: left - mouthRadius,
        right: right + mouthRadius,
        top: Math.min(root.y, innerY),
        bottom: Math.max(root.y, innerY),
        rootX: center,
        rootY: root.y,
        innerX: center,
        innerY,
        outlinePoints,
      };
    }

    const innerX = edge === 'right' ? root.x - screenDepth : root.x + screenDepth;
    const top = center - screenWidth / 2;
    const bottom = center + screenWidth / 2;
    const outlinePoints: number[] = [];

    if (edge === 'right') {
      addPathPoint(outlinePoints, root.x, top - mouthRadius);
      if (mouthRadius > 0) {
        addPathArc(
          outlinePoints,
          root.x - mouthRadius,
          top - mouthRadius,
          mouthRadius,
          0,
          Math.PI / 2,
        );
      }
      if (innerRadius > 0) {
        addPathPoint(outlinePoints, innerX + innerRadius, top);
        addPathArc(
          outlinePoints,
          innerX + innerRadius,
          top + innerRadius,
          innerRadius,
          -Math.PI / 2,
          -Math.PI,
        );
        addPathPoint(outlinePoints, innerX, bottom - innerRadius);
        addPathArc(
          outlinePoints,
          innerX + innerRadius,
          bottom - innerRadius,
          innerRadius,
          Math.PI,
          Math.PI / 2,
        );
      } else {
        addPathPoint(outlinePoints, innerX, top);
        addPathPoint(outlinePoints, innerX, bottom);
      }
      if (mouthRadius > 0) {
        addPathPoint(outlinePoints, root.x - mouthRadius, bottom);
        addPathArc(
          outlinePoints,
          root.x - mouthRadius,
          bottom + mouthRadius,
          mouthRadius,
          -Math.PI / 2,
          0,
        );
      } else {
        addPathPoint(outlinePoints, root.x, bottom);
      }
    } else {
      addPathPoint(outlinePoints, root.x, bottom + mouthRadius);
      if (mouthRadius > 0) {
        addPathArc(
          outlinePoints,
          root.x + mouthRadius,
          bottom + mouthRadius,
          mouthRadius,
          Math.PI,
          Math.PI * 1.5,
        );
      }
      if (innerRadius > 0) {
        addPathPoint(outlinePoints, innerX - innerRadius, bottom);
        addPathArc(
          outlinePoints,
          innerX - innerRadius,
          bottom - innerRadius,
          innerRadius,
          Math.PI / 2,
          0,
        );
        addPathPoint(outlinePoints, innerX, top + innerRadius);
        addPathArc(
          outlinePoints,
          innerX - innerRadius,
          top + innerRadius,
          innerRadius,
          0,
          -Math.PI / 2,
        );
      } else {
        addPathPoint(outlinePoints, innerX, bottom);
        addPathPoint(outlinePoints, innerX, top);
      }
      if (mouthRadius > 0) {
        addPathPoint(outlinePoints, root.x + mouthRadius, top);
        addPathArc(
          outlinePoints,
          root.x + mouthRadius,
          top - mouthRadius,
          mouthRadius,
          Math.PI / 2,
          Math.PI,
        );
      } else {
        addPathPoint(outlinePoints, root.x, top);
      }
    }

    return {
      edge,
      isHorizontal,
      centerX: innerX,
      centerY: center,
      width: screenDepth,
      depth: screenWidth,
      left: Math.min(root.x, innerX),
      right: Math.max(root.x, innerX),
      top: top - mouthRadius,
      bottom: bottom + mouthRadius,
      rootX: root.x,
      rootY: center,
      innerX,
      innerY: center,
      outlinePoints,
    };
  };
  const hasSelectedOutlineNotch =
    selectedOutlineNotchId !== null
      ? previewOutlineNotches.some(notch => notch.id === selectedOutlineNotchId)
      : false;
  const selectedFallbackTopNotchLabel =
    !hasSelectedOutlineNotch && selectedOutlineNotchId === 'top-notch' && doubleLinkMetrics
      ? {
          x: doubleLinkMetrics.notchCenter + 12,
          y: drawing.top + doubleLinkMetrics.notchDepth - 38,
          text: `zárez ${formatMm(
            (doubleLinkMetrics.notchDepth / Math.max(drawing.height, 1)) * config.lengthMm,
          )}`,
        }
      : null;

  const getSelectionBoxRect = (box: SelectionBox) => ({
    left: Math.min(box.startX, box.x),
    top: Math.min(box.startY, box.y),
    right: Math.max(box.startX, box.x),
    bottom: Math.max(box.startY, box.y),
  });

  const getHoleIndexesInSelectionBox = (box: SelectionBox) => {
    const rect = getSelectionBoxRect(box);

    return holes
      .map((hole, index) => ({
        index,
        point: interpolatePoint(positionShapePoints, hole.xRatio, hole.yRatio),
        radius: getHoleHitRadius(hole),
      }))
      .filter(
        ({ point, radius }) =>
          point.x + radius >= rect.left &&
          point.x - radius <= rect.right &&
          point.y + radius >= rect.top &&
          point.y - radius <= rect.bottom,
      )
      .map(({ index }) => index);
  };

  const getStagePointer = (event: any) => event.target.getStage()?.getPointerPosition() ?? null;

  const beginAreaSelection = (event: any) => {
    if (!update || event.target !== event.target.getStage()) return;

    const pointer = getStagePointer(event);
    if (!pointer) return;

    const baseIndexes = isAdditiveSelection(event) ? selectedIndexesRef.current : [];
    if (!baseIndexes.length) {
      setInternalCutSelection(null);
      setOutlineNotchSelection(null);
      setActiveBendLineId(null);
      setActiveShapeHandle(null);
    }
    selectionBaseIndexesRef.current = baseIndexes;
    setSelectionBox({ startX: pointer.x, startY: pointer.y, x: pointer.x, y: pointer.y });

    if (!baseIndexes.length) setSelection([], null);
  };

  const updateAreaSelection = (event: any) => {
    if (!selectionBox) return;

    const pointer = getStagePointer(event);
    if (!pointer) return;

    const nextBox = { ...selectionBox, x: pointer.x, y: pointer.y };
    setSelectionBox(nextBox);

    const width = Math.abs(nextBox.x - nextBox.startX);
    const height = Math.abs(nextBox.y - nextBox.startY);
    if (width < 4 && height < 4) return;

    const boxIndexes = getHoleIndexesInSelectionBox(nextBox);
    const nextIndexes = [...new Set([...selectionBaseIndexesRef.current, ...boxIndexes])].sort(
      (a, b) => a - b,
    );

    setSelection(nextIndexes, boxIndexes[boxIndexes.length - 1] ?? nextIndexes[0] ?? null);
  };

  const endAreaSelection = () => {
    if (!selectionBox) return;

    const width = Math.abs(selectionBox.x - selectionBox.startX);
    const height = Math.abs(selectionBox.y - selectionBox.startY);

    if (width < 4 && height < 4 && !selectionBaseIndexesRef.current.length) {
      setSelection([], null);
    }

    selectionBaseIndexesRef.current = [];
    setSelectionBox(null);
  };

  const getMinEdgeMm = (diameterMm = config.holeDiameterMm) => {
    void diameterMm;
    return 0;
  };

  const snapValue = (value: number) => {
    if (!snapStepMm || snapStepMm <= 0) return value;
    return Math.round(value / snapStepMm) * snapStepMm;
  };

  const normalizeMm = (value: number, shouldSnap = false) => {
    const nextValue = shouldSnap ? snapValue(value) : value;
    return roundGeometryMm(nextValue);
  };

  const clampHolePosition = (
    position: HolePosition,
    diameterMm = config.holeDiameterMm,
    shouldSnap = false,
  ) => {
    const minEdgeMm = getMinEdgeMm(diameterMm);
    const nextPosition = shouldSnap
      ? { xMm: snapValue(position.xMm), yMm: snapValue(position.yMm) }
      : position;
    return {
      xMm: normalizeMm(
        clamp(nextPosition.xMm, minEdgeMm, Math.max(minEdgeMm, config.widthMm - minEdgeMm)),
        shouldSnap,
      ),
      yMm: normalizeMm(
        clamp(nextPosition.yMm, minEdgeMm, Math.max(minEdgeMm, config.lengthMm - minEdgeMm)),
        shouldSnap,
      ),
    };
  };

  const screenToHolePosition = (
    x: number,
    y: number,
    diameterMm = config.holeDiameterMm,
    shouldSnap = true,
  ) =>
    clampHolePosition(
      {
        xMm: clamp((x - drawing.left) / Math.max(drawing.width, 1), 0, 1) * config.widthMm,
        yMm: clamp((y - drawing.top) / Math.max(drawing.height, 1), 0, 1) * config.lengthMm,
      },
      diameterMm,
      shouldSnap,
    );

  const holeToPoint = (xMm: number, yMm: number) =>
    interpolatePoint(
      positionShapePoints,
      xMm / Math.max(config.widthMm, 1),
      yMm / Math.max(config.lengthMm, 1),
    );

  const clampCutPosition = (position: HolePosition, shouldSnap = false, shouldRound = true) => {
    const nextPosition = shouldSnap
      ? { xMm: snapValue(position.xMm), yMm: snapValue(position.yMm) }
      : position;
    const xMm = clamp(nextPosition.xMm, 0, config.widthMm);
    const yMm = clamp(nextPosition.yMm, 0, config.lengthMm);

    if (!shouldRound) return { xMm, yMm };

    return {
      xMm: normalizeMm(xMm, shouldSnap),
      yMm: normalizeMm(yMm, shouldSnap),
    };
  };

  const screenToCutPosition = (x: number, y: number, shouldSnap = false, shouldRound = true) =>
    clampCutPosition(
      {
        xMm: clamp((x - drawing.left) / Math.max(drawing.width, 1), 0, 1) * config.widthMm,
        yMm: clamp((y - drawing.top) / Math.max(drawing.height, 1), 0, 1) * config.lengthMm,
      },
      shouldSnap,
      shouldRound,
    );

  const cutToPoint = (xMm: number, yMm: number) => ({
    x: drawing.left + (xMm / Math.max(config.widthMm, 1)) * drawing.width,
    y: drawing.top + (yMm / Math.max(config.lengthMm, 1)) * drawing.height,
  });

  const serializeInternalCut = (
    cut: ProductInternalCut,
    patch: Partial<ProductInternalCut> = {},
  ) => ({
    id: cut.id,
    label: patch.label ?? cut.label,
    shape: patch.shape ?? cut.shape,
    xMm: patch.xMm ?? cut.xMm,
    yMm: patch.yMm ?? cut.yMm,
    widthMm: patch.widthMm ?? cut.widthMm,
    lengthMm: patch.lengthMm ?? cut.lengthMm,
    orientation: patch.orientation ?? cut.orientation,
    angleDeg: patch.angleDeg ?? cut.angleDeg,
    cornerRadiusMm: patch.cornerRadiusMm ?? cut.cornerRadiusMm,
  });

  const commitInternalCutPosition = (cutId: string, position: HolePosition) => {
    if (!update) return;
    update({
      internalCuts: internalCuts.map(cut =>
        cut.id === cutId
          ? serializeInternalCut(cut, { xMm: position.xMm, yMm: position.yMm })
          : serializeInternalCut(cut),
      ),
    });
  };

  const updateCutGuideNodes = (
    cut: ProductInternalCut,
    box: ReturnType<typeof getCutRenderBoxAt>,
    position: HolePosition,
  ) => {
    const refs = cutGuideRefs.current;
    refs.leftArrow?.points([drawing.left, box.y, box.left, box.y]);
    refs.rightArrow?.points([drawing.left + drawing.width, box.y, box.right, box.y]);
    refs.topArrow?.points([box.x, drawing.top, box.x, box.top]);
    refs.bottomArrow?.points([box.x, drawing.top + drawing.height, box.x, box.bottom]);

    const measurements = getCutEdgeMeasurements(cut, position);
    const setLabel = (key: string, x: number, y: number, label: string) => {
      const node = refs[key];
      if (!node) return;
      node.position({ x, y });
      if (cutLabelValuesRef.current[key] !== label) {
        node.findOne('Text')?.text(label);
        cutLabelValuesRef.current[key] = label;
      }
    };

    void cut;
    setLabel(
      'leftLabel',
      (drawing.left + box.left) / 2 - 18,
      box.y - 32,
      formatMm(measurements.leftMm),
    );
    setLabel(
      'rightLabel',
      (box.right + drawing.left + drawing.width) / 2 - 18,
      box.y + 14,
      formatMm(measurements.rightMm),
    );
    setLabel('topLabel', box.x + 12, (drawing.top + box.top) / 2 - 7, formatMm(measurements.topMm));
    setLabel(
      'bottomLabel',
      box.x - 58,
      (box.bottom + drawing.top + drawing.height) / 2 - 7,
      formatMm(measurements.bottomMm),
    );
    requestCutGuideDraw();
  };

  const moveInternalCutPreview = (
    cut: ProductInternalCut,
    x: number,
    y: number,
    node: any,
    shouldSnap = false,
  ) => {
    const nextPosition = screenToCutPosition(x, y, shouldSnap, shouldSnap);
    const nextPoint = cutToPoint(nextPosition.xMm, nextPosition.yMm);
    const nextBox = getCutRenderBoxAt(cut, nextPosition.xMm, nextPosition.yMm);

    node.position(nextPoint);
    updateCutGuideNodes(cut, nextBox, nextPosition);
    pendingCutKeyboardPositionRef.current = { id: cut.id, ...nextPosition };
    didDragRef.current = true;
    return nextPosition;
  };

  const moveBendLine = (
    lineId: string,
    orientation: 'horizontal' | 'vertical',
    x: number,
    y: number,
  ) => {
    if (!update) return;

    const positionMm =
      orientation === 'horizontal'
        ? clamp((y - drawing.top) / Math.max(drawing.height, 1), 0, 1) * config.lengthMm
        : clamp((x - drawing.left) / Math.max(drawing.width, 1), 0, 1) * config.widthMm;

    update({
      bendLines: bendLines.map(line => ({
        id: line.id,
        label: line.label,
        orientation: line.orientation,
        positionMm:
          line.id === lineId ? roundGeometryMm(positionMm) : roundGeometryMm(line.positionMm),
        angleDeg: line.angleDeg,
        direction: line.direction,
      })),
    });
  };

  const updatePrimaryTopNotch = (patch: {
    widthMm?: number;
    depthMm?: number;
    radiusMm?: number;
    mouthRadiusMm?: number;
  }) => {
    if (!update) return;

    const notches = getProductOutlineNotches(config);
    const topNotchIndex = notches.findIndex(notch => notch.edge === 'top');
    const baseNotches =
      topNotchIndex >= 0
        ? notches
        : [
            {
              id: 'top-notch',
              label: 'Horný zárez',
              edge: 'top' as const,
              centerMm: config.widthMm / 2,
              centerRatio: 0.5,
              widthMm: config.outlineCenterGapMm ?? 12,
              depthMm: config.outlineTopNotchDepthMm ?? 24,
              radiusMm: 0,
              mouthRadiusMm: 0,
            },
          ];
    const targetIndex = topNotchIndex >= 0 ? topNotchIndex : 0;
    const nextNotches = baseNotches.map((notch, index) =>
      index === targetIndex
        ? {
            id: notch.id,
            label: notch.label,
            edge: notch.edge,
            centerMm: notch.centerMm,
            widthMm: patch.widthMm ?? notch.widthMm,
            depthMm: patch.depthMm ?? notch.depthMm,
            radiusMm: patch.radiusMm ?? notch.radiusMm,
            mouthRadiusMm: patch.mouthRadiusMm ?? notch.mouthRadiusMm,
          }
        : {
            id: notch.id,
            label: notch.label,
            edge: notch.edge,
            centerMm: notch.centerMm,
            widthMm: notch.widthMm,
            depthMm: notch.depthMm,
            radiusMm: notch.radiusMm,
            mouthRadiusMm: notch.mouthRadiusMm,
          },
    );
    const primary = nextNotches[targetIndex];

    update({
      outlineNotches: nextNotches,
      outlineCenterGapMm: primary.widthMm,
      outlineTopNotchDepthMm: primary.depthMm,
    });
  };

  const updateTopNotchFromScreenY = (y: number) => {
    if (!update) return;
    const nextDepthMm =
      Math.round(
        clamp((y - drawing.top) / Math.max(drawing.height, 1), 0, 0.6) * config.lengthMm * 10,
      ) / 10;
    updatePrimaryTopNotch({ depthMm: nextDepthMm });
  };

  const updateActiveGuideNodes = (point: { x: number; y: number }, position: HolePosition) => {
    const refs = activeGuideRefs.current;
    refs.leftArrow?.points([drawing.left, point.y, point.x, point.y]);
    refs.rightArrow?.points([drawing.left + drawing.width, point.y, point.x, point.y]);
    refs.topArrow?.points([point.x, drawing.top, point.x, point.y]);
    refs.bottomArrow?.points([point.x, drawing.top + drawing.height, point.x, point.y]);

    const setLabel = (key: string, x: number, y: number, label: string) => {
      const node = refs[key];
      if (!node) return;
      node.position({ x, y });
      if (activeLabelValuesRef.current[key] !== label) {
        node.findOne('Text')?.text(label);
        activeLabelValuesRef.current[key] = label;
      }
    };

    setLabel('leftLabel', (drawing.left + point.x) / 2 - 18, point.y - 32, formatMm(position.xMm));
    setLabel(
      'rightLabel',
      (point.x + drawing.left + drawing.width) / 2 - 18,
      point.y + 14,
      formatMm(config.widthMm - position.xMm),
    );
    setLabel('topLabel', point.x + 12, (drawing.top + point.y) / 2 - 7, formatMm(position.yMm));
    setLabel(
      'bottomLabel',
      point.x - 58,
      (point.y + drawing.top + drawing.height) / 2 - 7,
      formatMm(config.lengthMm - position.yMm),
    );

    interactiveLayerRef.current?.batchDraw();
  };

  const registerHoleNode = (index: number, node: any) => {
    holeNodeRefs.current[index] = node;
  };

  const handleHolePress = (index: number, event: any) => {
    const selected = selectedIndexesRef.current;
    const additive = isAdditiveSelection(event);

    if (additive && !selected.includes(index)) {
      setSelection([...selected, index], index);
      return;
    }

    if (!additive && !selected.includes(index)) {
      setSelection([index], index);
      return;
    }

    setSelection(selected, index);
  };

  const handleHoleClick = (index: number, event: any) => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }

    const selected = selectedIndexesRef.current;
    if (isAdditiveSelection(event)) {
      const nextSelection = selected.includes(index)
        ? selected.filter(selectedIndex => selectedIndex !== index)
        : [...selected, index];
      setSelection(
        nextSelection,
        nextSelection.includes(index) ? index : (nextSelection[0] ?? null),
      );
      return;
    }

    setSelection([index], index);
  };

  const startHoleDrag = (index: number, node: any) => {
    const selected = selectedIndexesRef.current.includes(index)
      ? selectedIndexesRef.current
      : [index];
    const positions = holes.map(hole => ({ xMm: hole.xMm, yMm: hole.yMm }));
    dragSnapshotRef.current = {
      anchorIndex: index,
      indexes: selected,
      positions,
    };
    setIsMeasurementDragActive(true);
    didDragRef.current = false;
    pendingDragPositionsRef.current = positions;
    setSelection(selected, index);
    node.position(holeToPoint(positions[index].xMm, positions[index].yMm));
    spacingMeasurementGroupRef.current?.visible(false);
    interactiveLayerRef.current?.batchDraw();
  };

  const moveHoleSelection = (
    index: number,
    x: number,
    y: number,
    node: any,
    shouldSnap = false,
  ) => {
    const snapshot = dragSnapshotRef.current ?? {
      anchorIndex: index,
      indexes: selectedIndexesRef.current.includes(index) ? selectedIndexesRef.current : [index],
      positions: holes.map(hole => ({ xMm: hole.xMm, yMm: hole.yMm })),
    };
    const anchorStart = snapshot.positions[snapshot.anchorIndex];
    if (!anchorStart) return null;

    const requestedAnchor = screenToHolePosition(
      x,
      y,
      holes[snapshot.anchorIndex]?.diameterMm,
      shouldSnap,
    );
    const minDeltaX = Math.max(
      ...snapshot.indexes.map(selectedIndex => {
        const minEdgeMm = getMinEdgeMm(holes[selectedIndex]?.diameterMm);
        return minEdgeMm - snapshot.positions[selectedIndex].xMm;
      }),
    );
    const maxDeltaX = Math.min(
      ...snapshot.indexes.map(selectedIndex => {
        const minEdgeMm = getMinEdgeMm(holes[selectedIndex]?.diameterMm);
        return config.widthMm - minEdgeMm - snapshot.positions[selectedIndex].xMm;
      }),
    );
    const minDeltaY = Math.max(
      ...snapshot.indexes.map(selectedIndex => {
        const minEdgeMm = getMinEdgeMm(holes[selectedIndex]?.diameterMm);
        return minEdgeMm - snapshot.positions[selectedIndex].yMm;
      }),
    );
    const maxDeltaY = Math.min(
      ...snapshot.indexes.map(selectedIndex => {
        const minEdgeMm = getMinEdgeMm(holes[selectedIndex]?.diameterMm);
        return config.lengthMm - minEdgeMm - snapshot.positions[selectedIndex].yMm;
      }),
    );
    const deltaX = clamp(requestedAnchor.xMm - anchorStart.xMm, minDeltaX, maxDeltaX);
    const deltaY = clamp(requestedAnchor.yMm - anchorStart.yMm, minDeltaY, maxDeltaY);
    const nextPositions = snapshot.positions.map(position => ({ ...position }));

    snapshot.indexes.forEach(selectedIndex => {
      nextPositions[selectedIndex] = clampHolePosition(
        {
          xMm: snapshot.positions[selectedIndex].xMm + deltaX,
          yMm: snapshot.positions[selectedIndex].yMm + deltaY,
        },
        holes[selectedIndex]?.diameterMm,
        shouldSnap,
      );
    });

    snapshot.indexes.forEach(selectedIndex => {
      const nextPosition = nextPositions[selectedIndex];
      if (!nextPosition) return;
      const nextPoint = holeToPoint(nextPosition.xMm, nextPosition.yMm);
      const holeNode = selectedIndex === index ? node : holeNodeRefs.current[selectedIndex];
      holeNode?.position(nextPoint);
    });

    const anchorPosition = nextPositions[snapshot.anchorIndex];
    const anchorPoint = holeToPoint(anchorPosition.xMm, anchorPosition.yMm);
    updateActiveGuideNodes(anchorPoint, anchorPosition);
    pendingDragPositionsRef.current = nextPositions;
    didDragRef.current = true;
    return nextPositions;
  };

  const endHoleDrag = (index: number, x: number, y: number, node: any) => {
    const nextPositions =
      moveHoleSelection(index, x, y, node, true) ?? pendingDragPositionsRef.current;
    dragSnapshotRef.current = null;
    pendingDragPositionsRef.current = null;
    setIsMeasurementDragActive(false);
    spacingMeasurementGroupRef.current?.visible(true);
    interactiveLayerRef.current?.batchDraw();
    if (update && nextPositions) update({ holePatternId: 'custom', holePositions: nextPositions });
  };

  const flushKeyboardMove = () => {
    if (keyboardCommitTimerRef.current) {
      clearTimeout(keyboardCommitTimerRef.current);
      keyboardCommitTimerRef.current = null;
    }

    const nextPositions = pendingKeyboardPositionsRef.current;
    pendingKeyboardPositionsRef.current = null;
    setIsMeasurementDragActive(false);
    if (update && nextPositions) update({ holePatternId: 'custom', holePositions: nextPositions });
  };

  const flushCutKeyboardMove = () => {
    if (cutKeyboardCommitTimerRef.current) {
      clearTimeout(cutKeyboardCommitTimerRef.current);
      cutKeyboardCommitTimerRef.current = null;
    }

    const nextPosition = pendingCutKeyboardPositionRef.current;
    pendingCutKeyboardPositionRef.current = null;
    setIsMeasurementDragActive(false);
    if (update && nextPosition) commitInternalCutPosition(nextPosition.id, nextPosition);
  };

  const scheduleKeyboardMoveCommit = () => {
    if (keyboardCommitTimerRef.current) clearTimeout(keyboardCommitTimerRef.current);
    keyboardCommitTimerRef.current = setTimeout(flushKeyboardMove, 120);
  };

  const scheduleCutKeyboardMoveCommit = () => {
    if (cutKeyboardCommitTimerRef.current) clearTimeout(cutKeyboardCommitTimerRef.current);
    cutKeyboardCommitTimerRef.current = setTimeout(flushCutKeyboardMove, 120);
  };

  const moveSelectedHolesBy = (deltaX: number, deltaY: number) => {
    const selected = selectedIndexesRef.current;
    if (!selected.length) return;
    setIsMeasurementDragActive(true);

    const positions =
      pendingKeyboardPositionsRef.current ?? holes.map(hole => ({ xMm: hole.xMm, yMm: hole.yMm }));
    const minDeltaX = Math.max(
      ...selected.map(selectedIndex => {
        const minEdgeMm = getMinEdgeMm(holes[selectedIndex]?.diameterMm);
        return minEdgeMm - positions[selectedIndex].xMm;
      }),
    );
    const maxDeltaX = Math.min(
      ...selected.map(selectedIndex => {
        const minEdgeMm = getMinEdgeMm(holes[selectedIndex]?.diameterMm);
        return config.widthMm - minEdgeMm - positions[selectedIndex].xMm;
      }),
    );
    const minDeltaY = Math.max(
      ...selected.map(selectedIndex => {
        const minEdgeMm = getMinEdgeMm(holes[selectedIndex]?.diameterMm);
        return minEdgeMm - positions[selectedIndex].yMm;
      }),
    );
    const maxDeltaY = Math.min(
      ...selected.map(selectedIndex => {
        const minEdgeMm = getMinEdgeMm(holes[selectedIndex]?.diameterMm);
        return config.lengthMm - minEdgeMm - positions[selectedIndex].yMm;
      }),
    );
    const safeDeltaX = clamp(deltaX, minDeltaX, maxDeltaX);
    const safeDeltaY = clamp(deltaY, minDeltaY, maxDeltaY);

    if (safeDeltaX === 0 && safeDeltaY === 0) return;

    const nextPositions = positions.map(position => ({ ...position }));
    selected.forEach(selectedIndex => {
      nextPositions[selectedIndex] = clampHolePosition(
        {
          xMm: positions[selectedIndex].xMm + safeDeltaX,
          yMm: positions[selectedIndex].yMm + safeDeltaY,
        },
        holes[selectedIndex]?.diameterMm,
        true,
      );
    });

    selected.forEach(selectedIndex => {
      const nextPosition = nextPositions[selectedIndex];
      if (!nextPosition) return;
      holeNodeRefs.current[selectedIndex]?.position(
        holeToPoint(nextPosition.xMm, nextPosition.yMm),
      );
    });

    const guideIndex =
      activeHoleIndex !== null && selected.includes(activeHoleIndex)
        ? activeHoleIndex
        : (selected[0] ?? null);
    const guidePosition = guideIndex !== null ? nextPositions[guideIndex] : null;
    if (guidePosition)
      updateActiveGuideNodes(holeToPoint(guidePosition.xMm, guidePosition.yMm), guidePosition);

    pendingKeyboardPositionsRef.current = nextPositions;
    interactiveLayerRef.current?.batchDraw();
    scheduleKeyboardMoveCommit();
  };

  const moveSelectedInternalCutBy = (cut: ProductInternalCut, deltaX: number, deltaY: number) => {
    const base =
      pendingCutKeyboardPositionRef.current?.id === cut.id
        ? pendingCutKeyboardPositionRef.current
        : { id: cut.id, xMm: cut.xMm, yMm: cut.yMm };
    setIsMeasurementDragActive(true);
    const nextPosition = clampCutPosition(
      {
        xMm: base.xMm + deltaX,
        yMm: base.yMm + deltaY,
      },
      true,
    );
    const node = cutNodeRefs.current[cut.id];
    const nextPoint = cutToPoint(nextPosition.xMm, nextPosition.yMm);
    const nextBox = getCutRenderBoxAt(cut, nextPosition.xMm, nextPosition.yMm);

    node?.position(nextPoint);
    updateCutGuideNodes(cut, nextBox, nextPosition);
    pendingCutKeyboardPositionRef.current = { id: cut.id, ...nextPosition };
    scheduleCutKeyboardMoveCommit();
  };

  const removeSelectedInternalCut = () => {
    if (!update || !selectedInternalCutId) return;
    update({
      internalCuts: internalCuts
        .filter(cut => cut.id !== selectedInternalCutId)
        .map(cut => serializeInternalCut(cut)),
    });
    setInternalCutSelection(null);
  };

  const syncOutlineNotchPatch = (
    notches: ReturnType<typeof getProductOutlineNotches>,
    targetId: string,
    patch: Partial<(typeof notches)[number]>,
  ) => {
    const serializeNotch = (
      notch: (typeof notches)[number],
      override: Partial<(typeof notches)[number]> = {},
    ) => {
      const edge = override.edge ?? notch.edge;
      const axisSize = getNotchAxisSize(edge);
      const widthMm = override.widthMm ?? notch.widthMm;
      const centerMm = clamp(
        override.centerMm ?? notch.centerMm,
        widthMm / 2,
        Math.max(widthMm / 2, axisSize - widthMm / 2),
      );

      return {
        id: notch.id,
        label: override.label ?? notch.label,
        edge,
        centerMm,
        centerRatio: centerMm / Math.max(axisSize, 1),
        widthMm,
        depthMm: override.depthMm ?? notch.depthMm,
        radiusMm: override.radiusMm ?? notch.radiusMm,
        mouthRadiusMm: override.mouthRadiusMm ?? notch.mouthRadiusMm,
      };
    };
    const nextNotches = notches.map(notch =>
      notch.id === targetId ? serializeNotch(notch, patch) : serializeNotch(notch),
    );
    const firstTopNotch = nextNotches.find(notch => notch.edge === 'top');

    return {
      outlineNotches: nextNotches,
      ...(firstTopNotch
        ? {
            outlineCenterGapMm: firstTopNotch.widthMm,
            outlineTopNotchDepthMm: firstTopNotch.depthMm,
          }
        : {}),
    };
  };

  const updateSelectedOutlineNotchBy = (
    notchId: string,
    deltaCenterMm: number,
    deltaDepthMm: number,
  ) => {
    if (!update) return;
    const notch = outlineNotches.find(item => item.id === notchId);
    if (!notch) return;
    const axisSize = getNotchAxisSize(notch.edge);
    const depthAxisSize = getNotchDepthAxisSize(notch.edge);

    update(
      syncOutlineNotchPatch(outlineNotches, notchId, {
        centerMm: clamp(
          notch.centerMm + deltaCenterMm,
          notch.widthMm / 2,
          axisSize - notch.widthMm / 2,
        ),
        depthMm: clamp(notch.depthMm + deltaDepthMm, 0, depthAxisSize * 0.75),
      }),
    );
  };

  const removeSelectedOutlineNotch = () => {
    if (!update || !selectedOutlineNotchId) return;
    const nextNotches = outlineNotches.filter(notch => notch.id !== selectedOutlineNotchId);
    const firstTopNotch = nextNotches.find(notch => notch.edge === 'top');
    update({
      outlineNotches: nextNotches.map(notch => ({
        id: notch.id,
        label: notch.label,
        edge: notch.edge,
        centerMm: notch.centerMm,
        widthMm: notch.widthMm,
        depthMm: notch.depthMm,
        radiusMm: notch.radiusMm,
        mouthRadiusMm: notch.mouthRadiusMm,
      })),
      ...(firstTopNotch
        ? {
            outlineCenterGapMm: firstTopNotch.widthMm,
            outlineTopNotchDepthMm: firstTopNotch.depthMm,
          }
        : {}),
    });
    setOutlineNotchSelection(null);
  };

  const removeSelectedHoles = () => {
    if (!update) return;

    const selected = selectedIndexesRef.current;
    if (!selected.length) return;

    const selectedSet = new Set(selected);
    const remainingHoles = holes.filter((_, index) => !selectedSet.has(index));
    const nextPositions = remainingHoles.map(hole => ({ xMm: hole.xMm, yMm: hole.yMm }));
    const nextDiameters = remainingHoles.map(hole => hole.diameterMm);
    const nextShapes = remainingHoles.map(hole => hole.shape);
    const nextSlotLengths = remainingHoles.map(hole => hole.slotLengthMm);
    const nextSlotOrientations = remainingHoles.map(hole => hole.slotOrientation);
    const nextSlotAngles = remainingHoles.map(hole => hole.slotAngleDeg);

    setSelection([], null);
    update({
      holeCount: nextPositions.length,
      holePatternId: 'custom',
      holePositions: nextPositions,
      holeDiameters: nextDiameters,
      holeShapes: nextShapes,
      holeSlotLengths: nextSlotLengths,
      holeSlotOrientations: nextSlotOrientations,
      holeSlotAnglesDeg: nextSlotAngles,
    });
  };

  const clampHoleDiameter = (value: number) =>
    roundGeometryMm(clamp(value, limits.holeDiameterMm.min, limits.holeDiameterMm.max));

  const setSelectedHoleDiameters = (diameterMm: number) => {
    if (!update) return;

    const selected = selectedIndexesRef.current;
    if (!selected.length) return;

    const nextDiameters = holes.map(hole => hole.diameterMm);
    const nextPositions = holes.map(hole => ({ xMm: hole.xMm, yMm: hole.yMm }));
    const nextSlotLengths = holes.map(hole => hole.slotLengthMm);
    const nextSlotOrientations = holes.map(hole => hole.slotOrientation);
    const nextSlotAngles = holes.map(hole => hole.slotAngleDeg);
    const nextDiameter = clampHoleDiameter(diameterMm);

    selected.forEach(selectedIndex => {
      nextDiameters[selectedIndex] = nextDiameter;
      nextSlotLengths[selectedIndex] = Math.max(nextSlotLengths[selectedIndex], nextDiameter);
      nextPositions[selectedIndex] = clampHolePosition(nextPositions[selectedIndex], nextDiameter);
    });

    const uniqueDiameters = new Set(nextDiameters.map(value => Math.round(value * 1000)));
    const uniqueSlotLengths = new Set(nextSlotLengths.map(value => Math.round(value * 1000)));

    update({
      holeDiameters: nextDiameters,
      holeSlotLengths: nextSlotLengths,
      holeSlotOrientations: nextSlotOrientations,
      holeSlotAnglesDeg: nextSlotAngles,
      holePatternId: 'custom',
      holePositions: nextPositions,
      ...(uniqueDiameters.size === 1 ? { holeDiameterMm: nextDiameters[0] } : {}),
      ...(uniqueSlotLengths.size === 1 ? { holeSlotLengthMm: nextSlotLengths[0] } : {}),
    });
  };

  const adjustSelectedHoleDiameters = (deltaMm: number) => {
    const selected = selectedIndexesRef.current;
    if (!selected.length) return;

    const baseDiameter = holes[activeHoleIndex ?? selected[0]]?.diameterMm ?? config.holeDiameterMm;
    setSelectedHoleDiameters(baseDiameter + deltaMm);
  };

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableKeyboardTarget(event.target)) return;

      const step = event.shiftKey ? 10 : event.altKey ? 0.5 : 1;
      const selectedCut =
        selectedInternalCutId !== null
          ? (internalCuts.find(cut => cut.id === selectedInternalCutId) ?? null)
          : null;
      const selectedNotch =
        selectedOutlineNotchId !== null
          ? (outlineNotches.find(notch => notch.id === selectedOutlineNotchId) ?? null)
          : null;

      if (selectedCut) {
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault();
          flushCutKeyboardMove();
          removeSelectedInternalCut();
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          moveSelectedInternalCutBy(selectedCut, -step, 0);
          return;
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          moveSelectedInternalCutBy(selectedCut, step, 0);
          return;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          moveSelectedInternalCutBy(selectedCut, 0, -step);
          return;
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          moveSelectedInternalCutBy(selectedCut, 0, step);
          return;
        }
      }

      if (selectedNotch) {
        const isHorizontalNotch = isHorizontalOutlineEdge(selectedNotch.edge);
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault();
          removeSelectedOutlineNotch();
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          updateSelectedOutlineNotchBy(
            selectedNotch.id,
            isHorizontalNotch ? -step : 0,
            isHorizontalNotch ? 0 : selectedNotch.edge === 'right' ? step : -step,
          );
          return;
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          updateSelectedOutlineNotchBy(
            selectedNotch.id,
            isHorizontalNotch ? step : 0,
            isHorizontalNotch ? 0 : selectedNotch.edge === 'right' ? -step : step,
          );
          return;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          updateSelectedOutlineNotchBy(
            selectedNotch.id,
            isHorizontalNotch ? 0 : -step,
            isHorizontalNotch ? (selectedNotch.edge === 'bottom' ? step : -step) : 0,
          );
          return;
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          updateSelectedOutlineNotchBy(
            selectedNotch.id,
            isHorizontalNotch ? 0 : step,
            isHorizontalNotch ? (selectedNotch.edge === 'bottom' ? -step : step) : 0,
          );
          return;
        }
      }

      if (!selectedIndexesRef.current.length) return;

      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        flushKeyboardMove();
        removeSelectedHoles();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveSelectedHolesBy(-step, 0);
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveSelectedHolesBy(step, 0);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveSelectedHolesBy(0, -step);
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveSelectedHolesBy(0, step);
        return;
      }

      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        flushKeyboardMove();
        adjustSelectedHoleDiameters(step);
        return;
      }

      if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        flushKeyboardMove();
        adjustSelectedHoleDiameters(-step);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown'
      ) {
        flushKeyboardMove();
        flushCutKeyboardMove();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  });

  return (
    <div ref={wrapperRef} className="relative h-full min-h-[260px] w-full overflow-hidden bg-white">
      {isActive && bendLines.length > 0 && (
        <button
          type="button"
          className={`absolute left-4 top-4 z-30 rounded-full border px-3 py-2 text-xs font-semibold shadow-sm transition ${
            visibleBendGuides
              ? 'border-[#2563eb] bg-[#eff6ff] text-[#1d4ed8]'
              : 'border-[#d9d9d9] bg-white/90 text-[#555] hover:border-black'
          }`}
          onClick={() => setLocalShowBendGuides(value => !value)}
          aria-pressed={visibleBendGuides}
        >
          {visibleBendGuides ? 'Ohyby zobrazené' : 'Zobraziť ohyby'}
        </button>
      )}
      <Stage
        width={size.width}
        height={size.height}
        onMouseDown={beginAreaSelection}
        onMouseMove={updateAreaSelection}
        onMouseUp={endAreaSelection}
        onMouseLeave={endAreaSelection}
        onTouchStart={beginAreaSelection}
        onTouchMove={updateAreaSelection}
        onTouchEnd={endAreaSelection}
      >
        <Layer listening={false}>
          {dots.map(dot => (
            <Circle key={`${dot.x}-${dot.y}`} x={dot.x} y={dot.y} radius={1.15} fill="#eeeeee" />
          ))}
          {constructionGrid.vertical.map(x => (
            <Line
              key={`grid-x-${x}`}
              points={[x, drawing.top, x, drawing.top + drawing.height]}
              stroke="#e6e6e6"
              strokeWidth={1}
              opacity={0.65}
            />
          ))}
          {constructionGrid.horizontal.map(y => (
            <Line
              key={`grid-y-${y}`}
              points={[drawing.left, y, drawing.left + drawing.width, y]}
              stroke="#e6e6e6"
              strokeWidth={1}
              opacity={0.65}
            />
          ))}
          {getPlanCornerRadius(previewConfig) > 0 && !isDoubleLinkProduct(previewConfig) && (
            <Rect
              x={drawing.left - 2}
              y={drawing.top - 2}
              width={drawing.width + 4}
              height={drawing.height + 4}
              fill="#fff"
              listening={false}
            />
          )}
          <Line
            points={flatten(shapePoints)}
            closed
            fill={finish.color}
            stroke="#171717"
            strokeWidth={2}
            shadowColor="rgba(0,0,0,0.12)"
            shadowBlur={18}
            shadowOffsetY={12}
          />
          <Line
            points={[
              drawing.left,
              drawing.top - 24,
              drawing.left + drawing.width,
              drawing.top - 24,
            ]}
            stroke="#1f2937"
            strokeWidth={1}
            dash={[5, 5]}
          />
          <Line
            points={[
              drawing.left + drawing.width + 32,
              drawing.top,
              drawing.left + drawing.width + 32,
              drawing.top + drawing.height,
            ]}
            stroke="#1f2937"
            strokeWidth={1}
            dash={[5, 5]}
          />
          <DimensionLabel
            x={drawing.left + drawing.width / 2 - 42}
            y={drawing.top - 48}
            label={`š: ${formatMm(config.widthMm)}`}
          />
          <DimensionLabel
            x={drawing.left + drawing.width + 44}
            y={drawing.top + drawing.height / 2 - 40}
            label={`d: ${formatMm(config.lengthMm)}`}
            rotation={90}
          />
        </Layer>

        <Layer ref={interactiveLayerRef}>
          {update &&
            previewOutlineNotches.map(notch => {
              const notchMetrics = getNotchScreenMetrics(notch);
              const notchWidth = notchMetrics.isHorizontal
                ? notchMetrics.width
                : notchMetrics.depth;
              const notchDepth = notchMetrics.isHorizontal
                ? notchMetrics.depth
                : notchMetrics.width;
              const centerX = notchMetrics.innerX;
              const handleY = notchMetrics.innerY;
              const measureY = notchMetrics.isHorizontal
                ? notch.edge === 'bottom'
                  ? Math.max(drawing.top + 20, notchMetrics.innerY - 34)
                  : Math.min(drawing.top + drawing.height - 20, notchMetrics.innerY + 34)
                : notchMetrics.innerY;
              const measureX = notchMetrics.isHorizontal
                ? notchMetrics.innerX
                : notch.edge === 'right'
                  ? Math.max(drawing.left + 20, notchMetrics.innerX - 34)
                  : Math.min(drawing.left + drawing.width - 20, notchMetrics.innerX + 34);
              const notchOutlinePoints = notchMetrics.outlinePoints;
              const isSelected = selectedOutlineNotchId === notch.id;
              const horizontalRootY = notch.edge === 'bottom' ? notchDepth : -notchDepth;
              const verticalRootX = notch.edge === 'right' ? notchDepth : -notchDepth;
              const horizontalDepthOnRight =
                notchMetrics.innerX < drawing.left + drawing.width * 0.72;
              const verticalDepthAbove = notchMetrics.innerY > drawing.top + drawing.height * 0.24;
              const horizontalDepthGuideX = horizontalDepthOnRight
                ? notchWidth / 2 + 18
                : -notchWidth / 2 - 18;
              const horizontalSizeLabelX = horizontalDepthOnRight
                ? notchWidth / 2 + 22
                : -notchWidth / 2 - 98;
              const horizontalSizeLabelY =
                horizontalRootY > 0 ? Math.max(12, horizontalRootY - 72) : horizontalRootY + 14;
              const verticalWidthGuideX = verticalRootX / 2;
              const verticalDepthGuideY = verticalDepthAbove
                ? -notchWidth / 2 - 24
                : notchWidth / 2 + 24;
              const verticalSizeLabelX =
                verticalRootX > 0 ? verticalRootX - 100 : verticalRootX + 16;
              const verticalSizeLabelY = verticalDepthAbove
                ? verticalDepthGuideY - 30
                : verticalDepthGuideY + 8;
              const getNotchPatchFromPoint = (x: number, y: number) => {
                const isHorizontal = isHorizontalOutlineEdge(notch.edge);
                const axisSize = getNotchAxisSize(notch.edge);
                const depthAxisSize = getNotchDepthAxisSize(notch.edge);
                const centerRatio = isHorizontal
                  ? clamp((x - drawing.left) / Math.max(drawing.width, 1), 0, 1)
                  : clamp((y - drawing.top) / Math.max(drawing.height, 1), 0, 1);
                const depthRatio =
                  notch.edge === 'bottom'
                    ? clamp(
                        (drawing.top + drawing.height - y) / Math.max(drawing.height, 1),
                        0,
                        0.75,
                      )
                    : notch.edge === 'right'
                      ? clamp(
                          (drawing.left + drawing.width - x) / Math.max(drawing.width, 1),
                          0,
                          0.75,
                        )
                      : notch.edge === 'left'
                        ? clamp((x - drawing.left) / Math.max(drawing.width, 1), 0, 0.75)
                        : clamp((y - drawing.top) / Math.max(drawing.height, 1), 0, 0.75);
                return {
                  centerMm: roundGeometryMm(centerRatio * axisSize),
                  depthMm: roundGeometryMm(depthRatio * depthAxisSize),
                };
              };
              const previewNotchFromPoint = (x: number, y: number) => {
                const patch = getNotchPatchFromPoint(x, y);
                const next = syncOutlineNotchPatch(previewOutlineNotches, notch.id, patch);
                setOutlineNotchPreview(next.outlineNotches);
              };
              const commitNotchFromPoint = (x: number, y: number) => {
                const patch = getNotchPatchFromPoint(x, y);
                const next = syncOutlineNotchPatch(outlineNotches, notch.id, patch);
                setOutlineNotchPreview(null);
                update(next);
              };

              return (
                <Group
                  key={notch.id}
                  x={centerX}
                  y={handleY}
                  draggable
                  dragBoundFunc={position => {
                    if (isHorizontalOutlineEdge(notch.edge)) {
                      return {
                        x: clamp(
                          position.x,
                          drawing.left + notchWidth / 2,
                          drawing.left + drawing.width - notchWidth / 2,
                        ),
                        y:
                          notch.edge === 'bottom'
                            ? clamp(
                                position.y,
                                drawing.top + drawing.height * 0.25,
                                drawing.top + drawing.height,
                              )
                            : clamp(position.y, drawing.top, drawing.top + drawing.height * 0.75),
                      };
                    }

                    return {
                      x:
                        notch.edge === 'right'
                          ? clamp(
                              position.x,
                              drawing.left + drawing.width * 0.25,
                              drawing.left + drawing.width,
                            )
                          : clamp(position.x, drawing.left, drawing.left + drawing.width * 0.75),
                      y: clamp(
                        position.y,
                        drawing.top + notchWidth / 2,
                        drawing.top + drawing.height - notchWidth / 2,
                      ),
                    };
                  }}
                  onMouseDown={event => {
                    event.cancelBubble = true;
                    setOutlineNotchSelection(notch.id);
                    setInternalCutSelection(null);
                    setSelection([], null);
                  }}
                  onClick={event => {
                    event.cancelBubble = true;
                    setOutlineNotchSelection(notch.id);
                    setInternalCutSelection(null);
                    setSelection([], null);
                  }}
                  onDragStart={event => {
                    event.cancelBubble = true;
                    setIsMeasurementDragActive(true);
                    setOutlineNotchSelection(notch.id);
                    setInternalCutSelection(null);
                    setSelection([], null);
                    setOutlineNotchPreview(previewOutlineNotches);
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = 'grabbing';
                  }}
                  onDragMove={event =>
                    previewNotchFromPoint(event.currentTarget.x(), event.currentTarget.y())
                  }
                  onDragEnd={event => {
                    commitNotchFromPoint(event.currentTarget.x(), event.currentTarget.y());
                    setIsMeasurementDragActive(false);
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = 'grab';
                  }}
                  onMouseEnter={event => {
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = 'grab';
                  }}
                  onMouseLeave={event => {
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = 'default';
                  }}
                >
                  <Rect
                    x={notchMetrics.left - centerX - 10}
                    y={notchMetrics.top - handleY - 10}
                    width={notchMetrics.right - notchMetrics.left + 20}
                    height={notchMetrics.bottom - notchMetrics.top + 20}
                    cornerRadius={10}
                    fill="rgba(230,50,8,0.001)"
                  />
                  {isSelected && (
                    <>
                      <Line
                        points={
                          notchMetrics.isHorizontal
                            ? [-notchWidth / 2, 0, notchWidth / 2, 0]
                            : [0, -notchWidth / 2, 0, notchWidth / 2]
                        }
                        stroke="#e63208"
                        strokeWidth={2.6}
                        dash={[7, 5]}
                        opacity={0.94}
                      />
                      <Line
                        points={notchOutlinePoints.map((value, index) =>
                          index % 2 === 0 ? value - centerX : value - handleY,
                        )}
                        stroke="#e63208"
                        strokeWidth={2.1}
                        lineCap="round"
                        lineJoin="round"
                        opacity={0.96}
                        listening={false}
                        perfectDrawEnabled={false}
                      />
                      <Circle radius={6} fill="#fff" stroke="#e63208" strokeWidth={2} opacity={1} />
                    </>
                  )}
                  {isSelected && notchMetrics.isHorizontal && (
                    <>
                      <Line
                        points={[drawing.left - centerX, measureY - handleY, 0, measureY - handleY]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        dash={[5, 4]}
                        listening={false}
                      />
                      <Line
                        points={[
                          0,
                          measureY - handleY,
                          drawing.left + drawing.width - centerX,
                          measureY - handleY,
                        ]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        dash={[5, 4]}
                        listening={false}
                      />
                      <Line
                        points={[0, measureY - handleY - 9, 0, measureY - handleY + 9]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <DimensionLabel
                        x={(drawing.left + centerX) / 2 - centerX - 22}
                        y={measureY - handleY - 18}
                        label={formatMm(Math.max(0, notch.centerMm))}
                        tone="active"
                      />
                      <DimensionLabel
                        x={(centerX + drawing.left + drawing.width) / 2 - centerX - 22}
                        y={measureY - handleY + 4}
                        label={formatMm(Math.max(0, config.widthMm - notch.centerMm))}
                        tone="active"
                      />
                      <DimensionLabel
                        x={horizontalSizeLabelX}
                        y={horizontalSizeLabelY}
                        label={`šírka ${formatMm(notch.widthMm)}`}
                        tone="active"
                      />
                      <Line
                        points={[horizontalDepthGuideX, horizontalRootY, horizontalDepthGuideX, 0]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <Line
                        points={[
                          horizontalDepthGuideX - 8,
                          horizontalRootY,
                          horizontalDepthGuideX + 8,
                          horizontalRootY,
                        ]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <Line
                        points={[horizontalDepthGuideX - 8, 0, horizontalDepthGuideX + 8, 0]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <DimensionLabel
                        x={horizontalSizeLabelX}
                        y={horizontalSizeLabelY + 28}
                        label={`hĺbka ${formatMm(notch.depthMm)}`}
                        tone="active"
                      />
                    </>
                  )}
                  {isSelected && !notchMetrics.isHorizontal && (
                    <>
                      <Line
                        points={[measureX - centerX, drawing.top - handleY, measureX - centerX, 0]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        dash={[5, 4]}
                        listening={false}
                      />
                      <Line
                        points={[
                          measureX - centerX,
                          0,
                          measureX - centerX,
                          drawing.top + drawing.height - handleY,
                        ]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        dash={[5, 4]}
                        listening={false}
                      />
                      <Line
                        points={[measureX - centerX - 9, 0, measureX - centerX + 9, 0]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <DimensionLabel
                        x={measureX - centerX + 10}
                        y={(drawing.top + handleY) / 2 - handleY - 7}
                        label={formatMm(Math.max(0, notch.centerMm))}
                        tone="active"
                      />
                      <DimensionLabel
                        x={measureX - centerX + 10}
                        y={(handleY + drawing.top + drawing.height) / 2 - handleY - 7}
                        label={formatMm(Math.max(0, config.lengthMm - notch.centerMm))}
                        tone="active"
                      />
                      <DimensionLabel
                        x={verticalSizeLabelX}
                        y={verticalSizeLabelY}
                        label={`šírka ${formatMm(notch.widthMm)}`}
                        tone="active"
                      />
                      <Line
                        points={[
                          verticalWidthGuideX,
                          -notchWidth / 2,
                          verticalWidthGuideX,
                          notchWidth / 2,
                        ]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <Line
                        points={[
                          verticalWidthGuideX - 8,
                          -notchWidth / 2,
                          verticalWidthGuideX + 8,
                          -notchWidth / 2,
                        ]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <Line
                        points={[
                          verticalWidthGuideX - 8,
                          notchWidth / 2,
                          verticalWidthGuideX + 8,
                          notchWidth / 2,
                        ]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <Line
                        points={[verticalRootX, verticalDepthGuideY, 0, verticalDepthGuideY]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <Line
                        points={[
                          verticalRootX,
                          verticalDepthGuideY - 8,
                          verticalRootX,
                          verticalDepthGuideY + 8,
                        ]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <Line
                        points={[0, verticalDepthGuideY - 8, 0, verticalDepthGuideY + 8]}
                        stroke="#e63208"
                        strokeWidth={0.9}
                        listening={false}
                      />
                      <DimensionLabel
                        x={verticalSizeLabelX}
                        y={verticalSizeLabelY + 28}
                        label={`hĺbka ${formatMm(notch.depthMm)}`}
                        tone="active"
                      />
                    </>
                  )}
                </Group>
              );
            })}
          {update && doubleLinkMetrics && previewOutlineNotches.length === 0 && (
            <>
              {(() => {
                const centerX = doubleLinkMetrics.notchCenter;
                const notchBottom = drawing.top + doubleLinkMetrics.notchDepth;
                const isTopSelected = selectedOutlineNotchId === 'top-notch';
                const isTopActive = activeShapeHandle === 'top-notch' || isTopSelected;

                return (
                  <>
                    <Group
                      x={centerX}
                      y={notchBottom}
                      draggable
                      dragBoundFunc={position => ({
                        x: centerX,
                        y: clamp(position.y, drawing.top, drawing.top + drawing.height * 0.6),
                      })}
                      onMouseDown={event => {
                        event.cancelBubble = true;
                        setActiveShapeHandle('top-notch');
                        setOutlineNotchSelection('top-notch');
                        setInternalCutSelection(null);
                        setSelection([], null);
                      }}
                      onDragStart={event => {
                        event.cancelBubble = true;
                        setIsMeasurementDragActive(true);
                        setActiveShapeHandle('top-notch');
                        setOutlineNotchSelection('top-notch');
                        setInternalCutSelection(null);
                        setSelection([], null);
                        const container = event.currentTarget.getStage()?.container();
                        if (container) container.style.cursor = 'ns-resize';
                      }}
                      onDragMove={event => updateTopNotchFromScreenY(event.currentTarget.y())}
                      onDragEnd={event => {
                        updateTopNotchFromScreenY(event.currentTarget.y());
                        setIsMeasurementDragActive(false);
                        const container = event.currentTarget.getStage()?.container();
                        if (container) container.style.cursor = 'grab';
                      }}
                      onMouseEnter={event => {
                        setActiveShapeHandle('top-notch');
                        const container = event.currentTarget.getStage()?.container();
                        if (container) container.style.cursor = 'ns-resize';
                      }}
                      onMouseLeave={event => {
                        setActiveShapeHandle(current => (current === 'top-notch' ? null : current));
                        const container = event.currentTarget.getStage()?.container();
                        if (container) container.style.cursor = 'default';
                      }}
                    >
                      <Rect
                        x={-(doubleLinkMetrics.centerGap / 2 + 20)}
                        y={-doubleLinkMetrics.notchDepth - 8}
                        width={doubleLinkMetrics.centerGap + 40}
                        height={doubleLinkMetrics.notchDepth + 22}
                        cornerRadius={12}
                        fill="rgba(230,50,8,0.001)"
                      />
                      {isTopActive && (
                        <>
                          <Line
                            points={[
                              -doubleLinkMetrics.centerGap / 2,
                              0,
                              doubleLinkMetrics.centerGap / 2,
                              0,
                            ]}
                            stroke="#e63208"
                            strokeWidth={2.8}
                            dash={[7, 5]}
                            opacity={0.92}
                          />
                          <Circle
                            radius={6}
                            fill="#fff"
                            stroke="#e63208"
                            strokeWidth={2}
                            opacity={1}
                          />
                        </>
                      )}
                    </Group>
                  </>
                );
              })()}
            </>
          )}

          {visibleBendGuides &&
            bendLines.map(line => {
              const isHorizontal = line.orientation === 'horizontal';
              const x =
                drawing.left +
                (isHorizontal
                  ? 0
                  : (line.positionMm / Math.max(config.widthMm, 1)) * drawing.width);
              const y =
                drawing.top +
                (isHorizontal
                  ? (line.positionMm / Math.max(config.lengthMm, 1)) * drawing.height
                  : 0);

              return (
                <Group
                  key={line.id}
                  x={isHorizontal ? 0 : x}
                  y={isHorizontal ? y : 0}
                  draggable={Boolean(update)}
                  dragBoundFunc={position =>
                    isHorizontal
                      ? {
                          x: 0,
                          y: clamp(position.y, drawing.top, drawing.top + drawing.height),
                        }
                      : {
                          x: clamp(position.x, drawing.left, drawing.left + drawing.width),
                          y: 0,
                        }
                  }
                  onMouseDown={event => {
                    event.cancelBubble = true;
                    setActiveBendLineId(line.id);
                  }}
                  onDragStart={event => {
                    event.cancelBubble = true;
                    setActiveBendLineId(line.id);
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = 'grabbing';
                  }}
                  onDragMove={event =>
                    moveBendLine(
                      line.id,
                      line.orientation,
                      event.currentTarget.x(),
                      event.currentTarget.y(),
                    )
                  }
                  onDragEnd={event => {
                    moveBendLine(
                      line.id,
                      line.orientation,
                      event.currentTarget.x(),
                      event.currentTarget.y(),
                    );
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = 'grab';
                  }}
                  onMouseEnter={event => {
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = update ? 'grab' : 'default';
                  }}
                  onMouseLeave={event => {
                    setActiveBendLineId(current => (current === line.id ? null : current));
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = 'default';
                  }}
                >
                  <Rect
                    x={isHorizontal ? drawing.left : -11}
                    y={isHorizontal ? -11 : drawing.top}
                    width={isHorizontal ? drawing.width : 22}
                    height={isHorizontal ? 22 : drawing.height}
                    fill="rgba(37,99,235,0.001)"
                  />
                  <Line
                    points={
                      isHorizontal
                        ? [drawing.left, 0, drawing.left + drawing.width, 0]
                        : [0, drawing.top, 0, drawing.top + drawing.height]
                    }
                    stroke="#2563eb"
                    strokeWidth={2}
                    dash={[9, 6, 2, 6]}
                    opacity={0.9}
                    listening={false}
                  />
                </Group>
              );
            })}

          {internalCuts.map(cut => {
            const cutBox = getCutRenderBox(cut);
            const hitPadding = 4;
            const isSelected = selectedInternalCutId === cut.id;
            return (
              <Group
                key={cut.id}
                ref={node => {
                  if (node) cutNodeRefs.current[cut.id] = node;
                }}
                transformsEnabled="position"
                x={cutBox.x}
                y={cutBox.y}
                draggable={Boolean(update)}
                dragBoundFunc={position => ({
                  x: clamp(position.x, drawing.left, drawing.left + drawing.width),
                  y: clamp(position.y, drawing.top, drawing.top + drawing.height),
                })}
                onMouseDown={event => {
                  event.cancelBubble = true;
                  setInternalCutSelection(cut.id);
                  setSelection([], null);
                }}
                onClick={event => {
                  event.cancelBubble = true;
                  setInternalCutSelection(cut.id);
                  setSelection([], null);
                }}
                onTap={event => {
                  event.cancelBubble = true;
                  setInternalCutSelection(cut.id);
                  setSelection([], null);
                }}
                onDragStart={event => {
                  event.cancelBubble = true;
                  setIsMeasurementDragActive(true);
                  setInternalCutSelection(cut.id);
                  setSelection([], null);
                  pendingCutKeyboardPositionRef.current = {
                    id: cut.id,
                    xMm: cut.xMm,
                    yMm: cut.yMm,
                  };
                  const container = event.currentTarget.getStage()?.container();
                  if (container) container.style.cursor = 'grabbing';
                }}
                onDragMove={event =>
                  moveInternalCutPreview(
                    cut,
                    event.currentTarget.x(),
                    event.currentTarget.y(),
                    event.currentTarget,
                  )
                }
                onDragEnd={event => {
                  const nextPosition = moveInternalCutPreview(
                    cut,
                    event.currentTarget.x(),
                    event.currentTarget.y(),
                    event.currentTarget,
                    true,
                  );
                  pendingCutKeyboardPositionRef.current = null;
                  commitInternalCutPosition(cut.id, nextPosition);
                  setIsMeasurementDragActive(false);
                  const container = event.currentTarget.getStage()?.container();
                  if (container) container.style.cursor = 'grab';
                }}
                onMouseEnter={event => {
                  const container = event.currentTarget.getStage()?.container();
                  if (container) container.style.cursor = update ? 'grab' : 'default';
                }}
                onMouseLeave={event => {
                  const container = event.currentTarget.getStage()?.container();
                  if (container) container.style.cursor = 'default';
                }}
              >
                <Rect
                  x={-cutBox.width / 2 - hitPadding}
                  y={-cutBox.height / 2 - hitPadding}
                  width={cutBox.width + hitPadding * 2}
                  height={cutBox.height + hitPadding * 2}
                  cornerRadius={Math.min(cutBox.width, cutBox.height) / 2 + hitPadding}
                  fill="rgba(37,99,235,0.001)"
                  perfectDrawEnabled={false}
                />
                <InternalCutGlyph
                  width={cutBox.renderWidth}
                  height={cutBox.renderHeight}
                  orientation={cut.orientation}
                  shape={cut.shape}
                  isSelected={isSelected}
                  angleDeg={cutBox.angleDeg}
                  cornerRadius={cutBox.cornerRadius}
                />
              </Group>
            );
          })}

          {holes.map((hole, index) => {
            const point = interpolatePoint(positionShapePoints, hole.xRatio, hole.yRatio);
            const isSelected = selectedHoleIndexes.includes(index);
            return update ? (
              <PlateHole
                key={`hole-${index}`}
                hole={hole}
                index={index}
                isActive={activeHoleIndex === index}
                isSelected={isSelected}
                point={point}
                radius={getHoleRadius(hole)}
                onClick={handleHoleClick}
                onDragEnd={endHoleDrag}
                onDragMove={moveHoleSelection}
                onDragStart={startHoleDrag}
                onNodeReady={registerHoleNode}
                onPress={handleHolePress}
              />
            ) : (
              <Group key={`hole-${index}`} x={point.x} y={point.y} listening={false}>
                <HoleGlyph hole={hole} radius={getHoleRadius(hole)} />
              </Group>
            );
          })}

          {selectedFallbackTopNotchLabel && (
            <Group listening={false}>
              <DimensionLabel
                x={selectedFallbackTopNotchLabel.x}
                y={selectedFallbackTopNotchLabel.y}
                label={selectedFallbackTopNotchLabel.text}
                tone="active"
              />
            </Group>
          )}

          {selectedCutBox && selectedCutMeasurements && (
            <Group listening={false}>
              <Arrow
                ref={node => {
                  if (node) cutGuideRefs.current.leftArrow = node;
                }}
                points={[drawing.left, selectedCutBox.y, selectedCutBox.left, selectedCutBox.y]}
                stroke="#e63208"
                fill="#e63208"
                strokeWidth={0.9}
                pointerLength={7}
                pointerWidth={6}
                pointerAtEnding
                opacity={0.82}
              />
              <Arrow
                ref={node => {
                  if (node) cutGuideRefs.current.rightArrow = node;
                }}
                points={[
                  drawing.left + drawing.width,
                  selectedCutBox.y,
                  selectedCutBox.right,
                  selectedCutBox.y,
                ]}
                stroke="#e63208"
                fill="#e63208"
                strokeWidth={0.9}
                pointerLength={7}
                pointerWidth={6}
                pointerAtEnding
                opacity={0.82}
              />
              <Arrow
                ref={node => {
                  if (node) cutGuideRefs.current.topArrow = node;
                }}
                points={[selectedCutBox.x, drawing.top, selectedCutBox.x, selectedCutBox.top]}
                stroke="#e63208"
                fill="#e63208"
                strokeWidth={0.9}
                pointerLength={7}
                pointerWidth={6}
                pointerAtEnding
                opacity={0.82}
              />
              <Arrow
                ref={node => {
                  if (node) cutGuideRefs.current.bottomArrow = node;
                }}
                points={[
                  selectedCutBox.x,
                  drawing.top + drawing.height,
                  selectedCutBox.x,
                  selectedCutBox.bottom,
                ]}
                stroke="#e63208"
                fill="#e63208"
                strokeWidth={0.9}
                pointerLength={7}
                pointerWidth={6}
                pointerAtEnding
                opacity={0.82}
              />
              <MeasurementLabel
                refKey="leftLabel"
                guideRefs={cutGuideRefs}
                x={(drawing.left + selectedCutBox.left) / 2 - 18}
                y={selectedCutBox.y - 32}
                label={formatMm(selectedCutMeasurements.leftMm)}
              />
              <MeasurementLabel
                refKey="rightLabel"
                guideRefs={cutGuideRefs}
                x={(selectedCutBox.right + drawing.left + drawing.width) / 2 - 18}
                y={selectedCutBox.y + 14}
                label={formatMm(selectedCutMeasurements.rightMm)}
              />
              <MeasurementLabel
                refKey="topLabel"
                guideRefs={cutGuideRefs}
                x={selectedCutBox.x + 12}
                y={(drawing.top + selectedCutBox.top) / 2 - 7}
                label={formatMm(selectedCutMeasurements.topMm)}
              />
              <MeasurementLabel
                refKey="bottomLabel"
                guideRefs={cutGuideRefs}
                x={selectedCutBox.x - 58}
                y={(selectedCutBox.bottom + drawing.top + drawing.height) / 2 - 7}
                label={formatMm(selectedCutMeasurements.bottomMm)}
              />
            </Group>
          )}

          {selectionBox && (
            <Rect
              x={Math.min(selectionBox.startX, selectionBox.x)}
              y={Math.min(selectionBox.startY, selectionBox.y)}
              width={Math.abs(selectionBox.x - selectionBox.startX)}
              height={Math.abs(selectionBox.y - selectionBox.startY)}
              cornerRadius={4}
              fill="rgba(230,50,8,0.08)"
              stroke="#e63208"
              strokeWidth={1.2}
              dash={[6, 5]}
              listening={false}
            />
          )}

          <Group
            ref={node => {
              spacingMeasurementGroupRef.current = node;
            }}
            listening={false}
          >
            {selectedHoleMeasurements.map(measurement => (
              <Group key={measurement.key} listening={false}>
                {measurement.guidePoints.map((points, index) => (
                  <Line
                    key={`${measurement.key}-guide-${index}`}
                    points={points}
                    stroke="#e63208"
                    strokeWidth={0.65}
                    opacity={0.38}
                  />
                ))}
                <Arrow
                  points={measurement.points}
                  stroke="#e63208"
                  fill="#e63208"
                  strokeWidth={0.85}
                  pointerLength={6}
                  pointerWidth={5}
                  pointerAtBeginning
                  pointerAtEnding
                  opacity={0.76}
                />
                <MeasurementLabel
                  x={measurement.labelX}
                  y={measurement.labelY}
                  label={measurement.label}
                />
              </Group>
            ))}
          </Group>
          {activeHole && activePoint && (
            <Group listening={false}>
              <Arrow
                ref={node => {
                  if (node) activeGuideRefs.current.leftArrow = node;
                }}
                points={[drawing.left, activePoint.y, activePoint.x, activePoint.y]}
                stroke="#e63208"
                fill="#e63208"
                strokeWidth={0.95}
                pointerLength={8}
                pointerWidth={7}
                pointerAtEnding
                opacity={0.88}
              />
              <Arrow
                ref={node => {
                  if (node) activeGuideRefs.current.rightArrow = node;
                }}
                points={[drawing.left + drawing.width, activePoint.y, activePoint.x, activePoint.y]}
                stroke="#e63208"
                fill="#e63208"
                strokeWidth={0.95}
                pointerLength={8}
                pointerWidth={7}
                pointerAtEnding
                opacity={0.88}
              />
              <Arrow
                ref={node => {
                  if (node) activeGuideRefs.current.topArrow = node;
                }}
                points={[activePoint.x, drawing.top, activePoint.x, activePoint.y]}
                stroke="#e63208"
                fill="#e63208"
                strokeWidth={0.95}
                pointerLength={8}
                pointerWidth={7}
                pointerAtEnding
                opacity={0.88}
              />
              <Arrow
                ref={node => {
                  if (node) activeGuideRefs.current.bottomArrow = node;
                }}
                points={[activePoint.x, drawing.top + drawing.height, activePoint.x, activePoint.y]}
                stroke="#e63208"
                fill="#e63208"
                strokeWidth={0.95}
                pointerLength={8}
                pointerWidth={7}
                pointerAtEnding
                opacity={0.88}
              />
              <MeasurementLabel
                refKey="leftLabel"
                guideRefs={activeGuideRefs}
                x={(drawing.left + activePoint.x) / 2 - 18}
                y={activePoint.y - 32}
                label={formatMm(activeHole.xMm)}
              />
              <MeasurementLabel
                refKey="rightLabel"
                guideRefs={activeGuideRefs}
                x={(activePoint.x + drawing.left + drawing.width) / 2 - 18}
                y={activePoint.y + 14}
                label={formatMm(config.widthMm - activeHole.xMm)}
              />
              <MeasurementLabel
                refKey="topLabel"
                guideRefs={activeGuideRefs}
                x={activePoint.x + 12}
                y={(drawing.top + activePoint.y) / 2 - 7}
                label={formatMm(activeHole.yMm)}
              />
              <MeasurementLabel
                refKey="bottomLabel"
                guideRefs={activeGuideRefs}
                x={activePoint.x - 58}
                y={(activePoint.y + drawing.top + drawing.height) / 2 - 7}
                label={formatMm(config.lengthMm - activeHole.yMm)}
              />
            </Group>
          )}
          {visibleBendGuides &&
            bendLines.map((line, bendIndex) => {
              if (activeBendLineId !== line.id) return null;

              const isHorizontal = line.orientation === 'horizontal';
              const lineX =
                drawing.left + (line.positionMm / Math.max(config.widthMm, 1)) * drawing.width;
              const lineY =
                drawing.top + (line.positionMm / Math.max(config.lengthMm, 1)) * drawing.height;

              return (
                <Label
                  key={`bend-label-${line.id}`}
                  x={isHorizontal ? drawing.left + 10 + (bendIndex % 3) * 72 : lineX + 8}
                  y={isHorizontal ? lineY - 28 : drawing.top + 10 + (bendIndex % 3) * 26}
                  listening={false}
                >
                  <Tag
                    fill="#ffffff"
                    stroke="#bfdbfe"
                    cornerRadius={5}
                    shadowColor="rgba(0,0,0,0.1)"
                    shadowBlur={8}
                    shadowOffsetY={2}
                  />
                  <Text
                    text={`${line.label} ${line.angleDeg}° ${line.direction === 'up' ? 'nahor' : 'nadol'}`}
                    padding={6}
                    fontSize={11}
                    fontStyle="700"
                    fill="#1d4ed8"
                  />
                </Label>
              );
            })}
        </Layer>
        {visibleBendGuides && (
          <Layer>
            {bendLines.map(line => {
              const isHorizontal = line.orientation === 'horizontal';
              const isActive = activeBendLineId === line.id;
              const x =
                drawing.left +
                (isHorizontal
                  ? 0
                  : (line.positionMm / Math.max(config.widthMm, 1)) * drawing.width);
              const y =
                drawing.top +
                (isHorizontal
                  ? (line.positionMm / Math.max(config.lengthMm, 1)) * drawing.height
                  : 0);
              const points = isHorizontal
                ? [drawing.left, 0, drawing.left + drawing.width, 0]
                : [0, drawing.top, 0, drawing.top + drawing.height];

              return (
                <Group
                  key={`bend-top-${line.id}`}
                  x={isHorizontal ? 0 : x}
                  y={isHorizontal ? y : 0}
                  draggable={Boolean(update)}
                  dragBoundFunc={position =>
                    isHorizontal
                      ? {
                          x: 0,
                          y: clamp(position.y, drawing.top, drawing.top + drawing.height),
                        }
                      : {
                          x: clamp(position.x, drawing.left, drawing.left + drawing.width),
                          y: 0,
                        }
                  }
                  onMouseDown={event => {
                    event.cancelBubble = true;
                    setActiveBendLineId(line.id);
                  }}
                  onTouchStart={event => {
                    event.cancelBubble = true;
                    setActiveBendLineId(line.id);
                  }}
                  onDragStart={event => {
                    event.cancelBubble = true;
                    setActiveBendLineId(line.id);
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = 'grabbing';
                  }}
                  onDragMove={event =>
                    moveBendLine(
                      line.id,
                      line.orientation,
                      event.currentTarget.x(),
                      event.currentTarget.y(),
                    )
                  }
                  onDragEnd={event => {
                    moveBendLine(
                      line.id,
                      line.orientation,
                      event.currentTarget.x(),
                      event.currentTarget.y(),
                    );
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = 'grab';
                  }}
                  onMouseEnter={event => {
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = update ? 'grab' : 'default';
                  }}
                  onMouseLeave={event => {
                    const container = event.currentTarget.getStage()?.container();
                    if (container) container.style.cursor = 'default';
                  }}
                >
                  <Line points={points} stroke="rgba(230,50,8,0.001)" strokeWidth={30} listening />
                  <Line
                    points={points}
                    stroke="#ffffff"
                    strokeWidth={isActive ? 6 : 5}
                    dash={[9, 6, 2, 6]}
                    opacity={0.82}
                    listening={false}
                  />
                  <Line
                    points={points}
                    stroke={isActive ? '#e63208' : '#2563eb'}
                    strokeWidth={isActive ? 3 : 2.3}
                    dash={[9, 6, 2, 6]}
                    opacity={1}
                    listening={false}
                  />
                </Group>
              );
            })}
          </Layer>
        )}
        <Layer listening={false}>
          {selectedOutlineNotchId &&
            previewOutlineNotches.map(notch => {
              if (notch.id !== selectedOutlineNotchId) return null;

              const notchMetrics = getNotchScreenMetrics(notch);
              const notchWidth = notchMetrics.isHorizontal
                ? notchMetrics.width
                : notchMetrics.depth;
              const notchDepth = notchMetrics.isHorizontal
                ? notchMetrics.depth
                : notchMetrics.width;
              const centerX = notchMetrics.innerX;
              const handleY = notchMetrics.innerY;
              const measureY = notchMetrics.isHorizontal
                ? notch.edge === 'bottom'
                  ? Math.max(drawing.top + 20, notchMetrics.innerY - 34)
                  : Math.min(drawing.top + drawing.height - 20, notchMetrics.innerY + 34)
                : notchMetrics.innerY;
              const measureX = notchMetrics.isHorizontal
                ? notchMetrics.innerX
                : notch.edge === 'right'
                  ? Math.max(drawing.left + 20, notchMetrics.innerX - 34)
                  : Math.min(drawing.left + drawing.width - 20, notchMetrics.innerX + 34);
              const horizontalRootY = notch.edge === 'bottom' ? notchDepth : -notchDepth;
              const verticalRootX = notch.edge === 'right' ? notchDepth : -notchDepth;
              const horizontalDepthOnRight =
                notchMetrics.innerX < drawing.left + drawing.width * 0.72;
              const verticalDepthAbove = notchMetrics.innerY > drawing.top + drawing.height * 0.24;
              const horizontalSizeLabelX = horizontalDepthOnRight
                ? notchWidth / 2 + 22
                : -notchWidth / 2 - 98;
              const horizontalSizeLabelY =
                horizontalRootY > 0 ? Math.max(12, horizontalRootY - 72) : horizontalRootY + 14;
              const verticalDepthGuideY = verticalDepthAbove
                ? -notchWidth / 2 - 24
                : notchWidth / 2 + 24;
              const verticalSizeLabelX =
                verticalRootX > 0 ? verticalRootX - 100 : verticalRootX + 16;
              const verticalSizeLabelY = verticalDepthAbove
                ? verticalDepthGuideY - 30
                : verticalDepthGuideY + 8;

              return (
                <Group key={`selected-notch-labels-${notch.id}`} x={centerX} y={handleY}>
                  {notchMetrics.isHorizontal ? (
                    <>
                      <DimensionLabel
                        x={(drawing.left + centerX) / 2 - centerX - 22}
                        y={measureY - handleY - 18}
                        label={formatMm(Math.max(0, notch.centerMm))}
                        tone="active"
                      />
                      <DimensionLabel
                        x={(centerX + drawing.left + drawing.width) / 2 - centerX - 22}
                        y={measureY - handleY + 4}
                        label={formatMm(Math.max(0, config.widthMm - notch.centerMm))}
                        tone="active"
                      />
                      <DimensionLabel
                        x={horizontalSizeLabelX}
                        y={horizontalSizeLabelY}
                        label={`šírka ${formatMm(notch.widthMm)}`}
                        tone="active"
                      />
                      <DimensionLabel
                        x={horizontalSizeLabelX}
                        y={horizontalSizeLabelY + 28}
                        label={`hĺbka ${formatMm(notch.depthMm)}`}
                        tone="active"
                      />
                    </>
                  ) : (
                    <>
                      <DimensionLabel
                        x={measureX - centerX + 10}
                        y={(drawing.top + handleY) / 2 - handleY - 7}
                        label={formatMm(Math.max(0, notch.centerMm))}
                        tone="active"
                      />
                      <DimensionLabel
                        x={measureX - centerX + 10}
                        y={(handleY + drawing.top + drawing.height) / 2 - handleY - 7}
                        label={formatMm(Math.max(0, config.lengthMm - notch.centerMm))}
                        tone="active"
                      />
                      <DimensionLabel
                        x={verticalSizeLabelX}
                        y={verticalSizeLabelY}
                        label={`šírka ${formatMm(notch.widthMm)}`}
                        tone="active"
                      />
                      <DimensionLabel
                        x={verticalSizeLabelX}
                        y={verticalSizeLabelY + 28}
                        label={`hĺbka ${formatMm(notch.depthMm)}`}
                        tone="active"
                      />
                    </>
                  )}
                </Group>
              );
            })}

          {!isMeasurementDragActive && selectedFallbackTopNotchLabel && (
            <DimensionLabel
              x={selectedFallbackTopNotchLabel.x}
              y={selectedFallbackTopNotchLabel.y}
              label={selectedFallbackTopNotchLabel.text}
              tone="active"
            />
          )}

          {!isMeasurementDragActive && selectedCutBox && selectedCutMeasurements && (
            <Group listening={false}>
              <MeasurementLabel
                x={(drawing.left + selectedCutBox.left) / 2 - 18}
                y={selectedCutBox.y - 32}
                label={formatMm(selectedCutMeasurements.leftMm)}
              />
              <MeasurementLabel
                x={(selectedCutBox.right + drawing.left + drawing.width) / 2 - 18}
                y={selectedCutBox.y + 14}
                label={formatMm(selectedCutMeasurements.rightMm)}
              />
              <MeasurementLabel
                x={selectedCutBox.x + 12}
                y={(drawing.top + selectedCutBox.top) / 2 - 7}
                label={formatMm(selectedCutMeasurements.topMm)}
              />
              <MeasurementLabel
                x={selectedCutBox.x - 58}
                y={(selectedCutBox.bottom + drawing.top + drawing.height) / 2 - 7}
                label={formatMm(selectedCutMeasurements.bottomMm)}
              />
            </Group>
          )}

          {!isMeasurementDragActive &&
            selectedHoleMeasurements.map(measurement => (
              <MeasurementLabel
                key={`selected-hole-label-top-${measurement.key}`}
                x={measurement.labelX}
                y={measurement.labelY}
                label={measurement.label}
              />
            ))}

          {!isMeasurementDragActive && activeHole && activePoint && (
            <Group listening={false}>
              <MeasurementLabel
                x={(drawing.left + activePoint.x) / 2 - 18}
                y={activePoint.y - 32}
                label={formatMm(activeHole.xMm)}
              />
              <MeasurementLabel
                x={(activePoint.x + drawing.left + drawing.width) / 2 - 18}
                y={activePoint.y + 14}
                label={formatMm(config.widthMm - activeHole.xMm)}
              />
              <MeasurementLabel
                x={activePoint.x + 12}
                y={(drawing.top + activePoint.y) / 2 - 7}
                label={formatMm(activeHole.yMm)}
              />
              <MeasurementLabel
                x={activePoint.x - 58}
                y={(activePoint.y + drawing.top + drawing.height) / 2 - 7}
                label={formatMm(config.lengthMm - activeHole.yMm)}
              />
            </Group>
          )}

          {visibleBendGuides &&
            bendLines.map((line, bendIndex) => {
              if (activeBendLineId !== line.id) return null;

              const isHorizontal = line.orientation === 'horizontal';
              const lineX =
                drawing.left + (line.positionMm / Math.max(config.widthMm, 1)) * drawing.width;
              const lineY =
                drawing.top + (line.positionMm / Math.max(config.lengthMm, 1)) * drawing.height;

              return (
                <Label
                  key={`bend-label-top-${line.id}`}
                  x={isHorizontal ? drawing.left + 10 + (bendIndex % 3) * 72 : lineX + 8}
                  y={isHorizontal ? lineY - 28 : drawing.top + 10 + (bendIndex % 3) * 26}
                  listening={false}
                >
                  <Tag
                    fill="#ffffff"
                    stroke="#bfdbfe"
                    cornerRadius={5}
                    shadowColor="rgba(0,0,0,0.1)"
                    shadowBlur={8}
                    shadowOffsetY={2}
                  />
                  <Text
                    text={`${line.label} ${line.angleDeg}° ${line.direction === 'up' ? 'nahor' : 'nadol'}`}
                    padding={6}
                    fontSize={11}
                    fontStyle="700"
                    fill="#1d4ed8"
                  />
                </Label>
              );
            })}
        </Layer>
      </Stage>
    </div>
  );
}

export function PlatePreview({
  config,
  viewMode,
  update,
  selectedHoleIndexes,
  activeHoleIndex,
  onHoleSelectionChange,
  selectedInternalCutId,
  onInternalCutSelectionChange,
  selectedOutlineNotchId,
  onOutlineNotchSelectionChange,
  snapStepMm,
  showBendGuides,
}: PlatePreviewProps) {
  const layerClass = (active: boolean) =>
    active
      ? 'absolute inset-0 h-full w-full opacity-100'
      : 'pointer-events-none invisible absolute inset-0 h-full w-full opacity-0';

  return (
    <div className="relative h-full min-h-[260px] w-full overflow-hidden">
      <div className={layerClass(viewMode === '3d')} aria-hidden={viewMode !== '3d'}>
        <PlatePreview3D config={config} />
      </div>
      <div className={layerClass(viewMode === '2d')} aria-hidden={viewMode !== '2d'}>
        <PlatePreview2D
          config={config}
          update={update}
          selectedHoleIndexes={selectedHoleIndexes}
          activeHoleIndex={activeHoleIndex}
          onHoleSelectionChange={onHoleSelectionChange}
          selectedInternalCutId={selectedInternalCutId}
          onInternalCutSelectionChange={onInternalCutSelectionChange}
          selectedOutlineNotchId={selectedOutlineNotchId}
          onOutlineNotchSelectionChange={onOutlineNotchSelectionChange}
          snapStepMm={snapStepMm}
          showBendGuides={showBendGuides}
          isActive={viewMode === '2d'}
        />
      </div>
    </div>
  );
}
